// src/components/Orders/Index.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { logClientActivity } from "../../utils/clientLogger";
import "material-design-icons/iconfont/material-icons.css";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { useOrders } from "./useOrders";
import { useDarkMode } from "../../hooks/useDarkMode";
import buildPrintTicket from "./PrintTicket";

import TicketCard from "./TicketCard";
import TopBar from "./TopBar";
import FooterBar from "./FooterBar";
import FreeformBoard from "./FreeformBoard";
import LineItemBoard from "./LineItemBoard";
import DndTicketBoard from "./DndTicketBoard";
import SettingsModal from "../Settings";

import { apiFetch, API_BASE } from "../../utils/api";
import socket from "../../socket";
import { getCardColorDiagnostic } from "./utils";

const PARK_MODE_MANUAL = "manual";
const PARK_MODE_SPECIFIC = "specific_time";
const PARK_MODE_DURATION = "after_duration";

const PRINT_SERVER_URL = "https://printserver.myods.club/api/api/jobs/escpos";
const FREEFORM_SPAWN_X = 24;
const FREEFORM_SPAWN_Y = 24;
const LINE_ITEM_SORT_OPTIONS = [
  { mode: "line-fired-time", label: "Time Fired First" },
  { mode: "line-ticket-number", label: "Ticket Number" },
  { mode: "line-course", label: "Course" },
  { mode: "line-ticket-most-items", label: "Most Items On Ticket" },
  { mode: "line-ticket-least-items", label: "Least Items On Ticket" },
];

const isDisplayableLineItem = (item) =>
  !!item &&
  !item.isCourseSeparator &&
  !item.isMessage &&
  !item.isFireItem &&
  !item.isBumped;

const isBumpBarIgnoredTarget = (target) => {
  const tagName = String(target?.tagName || "").toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    target?.isContentEditable
  );
};

const isBumpBarRealItem = (item = {}) => {
  const status = String(item?.status || "").toLowerCase();
  return !(
    !item ||
    item.isCourseSeparator ||
    item.isMessage ||
    item.isFireItem ||
    item.isVoided ||
    item.isParked ||
    Number(item.TransType) === 2 ||
    status === "voided" ||
    status === "parked"
  );
};

const isBumpBarSelectableTicket = (order = {}) => {
  if (!order || order.isParked) return false;
  const items = Array.isArray(order.items) ? order.items : [];
  const realItems = items.filter(isBumpBarRealItem);
  return realItems.length > 0;
};

const toSortableTime = (value, fallback = Number.MAX_SAFE_INTEGER) => {
  const time = new Date(value || 0).getTime();
  return Number.isFinite(time) && time > 0 ? time : fallback;
};

const compareTicketNumber = (left, right) => {
  const leftValue = left?.order?.order_id ?? left?.order?.ticket_instance_id ?? "";
  const rightValue = right?.order?.order_id ?? right?.order?.ticket_instance_id ?? "";
  return String(leftValue).localeCompare(String(rightValue), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const compareLineItemDefault = (left, right) => {
  const leftOrderTime = toSortableTime(left?.order?.createdAt ?? left?.order?.receivedAt);
  const rightOrderTime = toSortableTime(right?.order?.createdAt ?? right?.order?.receivedAt);
  if (leftOrderTime !== rightOrderTime) return leftOrderTime - rightOrderTime;

  const ticketDiff = compareTicketNumber(left, right);
  if (ticketDiff !== 0) return ticketDiff;

  return toSortableTime(left?.item?.createdAt) - toSortableTime(right?.item?.createdAt);
};

const buildLineItemRowsForOrders = (ordersToList) =>
  ordersToList
    .flatMap((order) =>
      (order?.items || [])
        .filter(isDisplayableLineItem)
        .map((item) => ({
          id: `${order.ticket_instance_id}::${item.ID}`,
          order,
          item,
        }))
    )
    .sort(compareLineItemDefault);

function getTimeZoneParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const read = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

function zonedDateTimeToUtc(parts, timeZone) {
  const baseUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
    0
  );

  const offsetParts = getTimeZoneParts(new Date(baseUtc), timeZone);
  const offsetUtc = Date.UTC(
    offsetParts.year,
    offsetParts.month - 1,
    offsetParts.day,
    offsetParts.hour,
    offsetParts.minute,
    offsetParts.second,
    0
  );

  return new Date(baseUtc - (offsetUtc - baseUtc));
}

function addDaysToLocalDate(parts, daysToAdd) {
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + daysToAdd));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

function buildScheduledUnparkIso({
  parkMode,
  stationTimezone,
  specificTime,
  durationHours,
  durationMinutes,
}) {
  if (parkMode === PARK_MODE_MANUAL) return null;

  if (parkMode === PARK_MODE_SPECIFIC) {
    if (!specificTime || !specificTime.includes(":")) return null;
    const [hourText, minuteText] = specificTime.split(":");
    const hour = Number(hourText);
    const minute = Number(minuteText);
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;

    const now = new Date();
    const nowLocal = getTimeZoneParts(now, stationTimezone);
    let localDate = {
      year: nowLocal.year,
      month: nowLocal.month,
      day: nowLocal.day,
    };

    let candidate = zonedDateTimeToUtc(
      { ...localDate, hour, minute, second: 0 },
      stationTimezone
    );

    if (candidate.getTime() <= now.getTime()) {
      localDate = addDaysToLocalDate(localDate, 1);
      candidate = zonedDateTimeToUtc(
        { ...localDate, hour, minute, second: 0 },
        stationTimezone
      );
    }

    return candidate.toISOString();
  }

  if (parkMode === PARK_MODE_DURATION) {
    const hours = Math.max(0, Number(durationHours) || 0);
    const minutes = Math.max(0, Number(durationMinutes) || 0);
    const totalMinutes = hours * 60 + minutes;
    if (totalMinutes <= 0) return null;
    return new Date(Date.now() + totalMinutes * 60 * 1000).toISOString();
  }

  return null;
}

function formatScheduledPreview(unparkIso, stationTimezone) {
  if (!unparkIso) return "Manual unpark";
  const parsed = new Date(unparkIso);
  if (Number.isNaN(parsed.getTime())) return "Invalid schedule";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: stationTimezone,
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  }).format(parsed);
}

function formatTimeInputInZone(date, timeZone) {
  const parts = getTimeZoneParts(date, timeZone);
  return `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}

export default function Index({ stationId, role, stationName, stationTimezone = "UTC", onOpenTutorial = null }) {
  const {
    orders,
    setOrders,
    fetchOrders,

    fireCourse,
    FireAllItems,
    emitFireTicket,

    emitBumpTicket,
    emitBumpCourse,
    emitBumpItem,
    emitBumpAllCoursesForTicket,
    emitUnbumpTicket,

    parkTicket,
    unparkTicket,
    fireItem,
    lastUnparkSignal,
    emitTicketLocationSync,
    emitTicketPageSync,
    screenMountId,

    settings,
  } = useOrders({
    stationId,
    role,
    stationTimezone,
    onVoidedItem: (payload = {}) => {
      const targetStationId = Number(payload?.station_id);
      if (Number.isFinite(targetStationId) && targetStationId !== Number(stationId)) return;
      playVoidedItemSoundOnce(payload);
    },
    onCourseFired: (payload = {}) => {
      const targetStationId = Number(payload?.station_id);
      if (Number.isFinite(targetStationId) && targetStationId !== Number(stationId)) return;
      playCourseFiredSoundOnce(payload);
    },
  });

  const { isDark, toggleDarkMode } = useDarkMode("kdsDarkMode");

  const [showSettings, setShowSettings] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const [isPeekMode, setIsPeekMode] = useState(false);
  const [peekedOrder, setPeekedOrder] = useState(null);
  const [peekModeIntent, setPeekModeIntent] = useState(null);

  const [isFireCourseMode, setIsFireCourseMode] = useState(false);
  const [isFireTicketMode, setIsFireTicketMode] = useState(false);
  const [isFireItemMode, setIsFireItemMode] = useState(false);
  const [isPrintCourseMode, setIsPrintCourseMode] = useState(false);
  const [isPrintTicketMode, setIsPrintTicketMode] = useState(false);
  const [isParkTicketMode, setIsParkTicketMode] = useState(false);
  const [isPinTicketMode, setIsPinTicketMode] = useState(false);

  const [isUnparkModalOpen, setIsUnparkModalOpen] = useState(false);
  const [selectedParkedTicketId, setSelectedParkedTicketId] = useState(null);
  const [isExposeModalOpen, setIsExposeModalOpen] = useState(false);
  const [selectedExposeTicketKey, setSelectedExposeTicketKey] = useState(null);
  const [isSortModalOpen, setIsSortModalOpen] = useState(false);
  const [isLineItemSortModalOpen, setIsLineItemSortModalOpen] = useState(false);
  const [freeformSortRequest, setFreeformSortRequest] = useState(null);
  const [isFreeformLockXAxis, setIsFreeformLockXAxis] = useState(false);
  const [isFreeformLockYAxis, setIsFreeformLockYAxis] = useState(false);
  const [pendingSortMode, setPendingSortMode] = useState(null);
  const [isParkOptionsModalOpen, setIsParkOptionsModalOpen] = useState(false);
  const [parkSelectionTicket, setParkSelectionTicket] = useState(null);
  const [parkMode, setParkMode] = useState(PARK_MODE_MANUAL);
  const [specificUnparkTime, setSpecificUnparkTime] = useState("");
  const [durationHours, setDurationHours] = useState("0");
  const [durationMinutes, setDurationMinutes] = useState("15");
  const [bumpBarSelectionMode, setBumpBarSelectionMode] = useState("ticket");
  const [bumpBarSelectedTicketKey, setBumpBarSelectedTicketKey] = useState(null);
  const [bumpBarSelectedItemId, setBumpBarSelectedItemId] = useState(null);
  const [bumpBarSelectedPageControl, setBumpBarSelectedPageControl] = useState(null);
  const [bumpBarSummarySignal, setBumpBarSummarySignal] = useState(0);
  const [bumpBarUnbumpMenuSignal, setBumpBarUnbumpMenuSignal] = useState(0);
  const durationPresetMinutes = [15, 30, 45, 60, 90, 120];
  const specificTimePresetMinutes = [15, 30, 60];

  const [isDragging, setIsDragging] = useState(false);
  const currentPageRef = useRef(0);
  const suppressSwipeUntilRef = useRef(0);
  const autoCourseParkTicketIdsRef = useRef(new Set());

  const TICKETS_PER_PAGE = 6;
  const [currentPage, setCurrentPage] = useState(0);
  const [isViewingFreeform, setIsViewingFreeform] = useState(false);
  const [isViewingLineItems, setIsViewingLineItems] = useState(false);

  const keyForOrder = (o) => `${o.ticket_instance_id}::${o.card ?? 0}`;

  const chunkIntoPages = (keys, size) => {
    const out = [];
    for (let i = 0; i < keys.length; i += size) out.push(keys.slice(i, i + size));
    return out.length ? out : [[]];
  };

  const [pages, setPages] = useState([[]]);
  const [freeformLayouts, setFreeformLayouts] = useState({});
  const [lineItemTicketKeys, setLineItemTicketKeys] = useState(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(`kds-line-item-tickets:${stationId}`) || "[]");
      return new Set(Array.isArray(stored) ? stored : []);
    } catch {
      return new Set();
    }
  });
  const [lineItemRowOrder, setLineItemRowOrder] = useState(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(`kds-line-item-row-order:${stationId}`) || "[]");
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  });
  const previousParkStateRef = useRef(new Map());
  const handledUnparkPromotionNonceRef = useRef(null);
  const autoMoveSeenTicketKeysRef = useRef(null);
  const freeformEnabled = settings?.enable_freeform_board === true;
  const lineItemBoardEnabled = settings?.enable_line_item_board === true;
  const keyForLayout = useCallback((ticketInstanceId, card = 0) => {
    return `${ticketInstanceId}::${Number.isFinite(Number(card)) ? Number(card) : 0}`;
  }, []);

  const orderByKey = useMemo(() => {
    const map = Object.create(null);
    for (const o of orders) map[keyForOrder(o)] = o;
    return map;
  }, [orders]);
  const freeformKeySet = useMemo(() => {
    const next = new Set();
    Object.keys(freeformLayouts).forEach((key) => {
      const order = orderByKey[key];
      if (order && !order?.isParked) {
        next.add(key);
      }
    });
    return next;
  }, [freeformLayouts, orderByKey]);
  const lineItemKeySet = useMemo(() => new Set(
    Array.from(lineItemTicketKeys).filter((key) => !!orderByKey[key] && !orderByKey[key]?.isParked)
  ), [lineItemTicketKeys, orderByKey]);
  const lineItemRowsForSort = useMemo(() => {
    return buildLineItemRowsForOrders(
      Array.from(lineItemKeySet).map((key) => orderByKey[key]).filter(Boolean)
    );
  }, [lineItemKeySet, orderByKey]);
  useEffect(() => {
    window.localStorage.setItem(`kds-line-item-tickets:${stationId}`, JSON.stringify(Array.from(lineItemTicketKeys)));
  }, [lineItemTicketKeys, stationId]);
  useEffect(() => {
    window.localStorage.setItem(`kds-line-item-row-order:${stationId}`, JSON.stringify(lineItemRowOrder));
  }, [lineItemRowOrder, stationId]);
  const isOnFreeformPage = freeformEnabled && isViewingFreeform;
  const isOnLineItemPage = lineItemBoardEnabled && isViewingLineItems;
  const specialPageCount = Number(freeformEnabled) + Number(lineItemBoardEnabled);
  const activePageIndex = isOnFreeformPage ? 0 : isOnLineItemPage ? Number(freeformEnabled) : currentPage + specialPageCount;
  const totalPages = Math.max(1, pages.length + specialPageCount);
  const suppressSwipeForDragHandle = useCallback((durationMs = 1500) => {
    suppressSwipeUntilRef.current = Date.now() + durationMs;
  }, []);

  useEffect(() => {
    setPages((prev) => {
      const known = new Set(prev.flat());

      const visibleOrders = orders.filter((o) => !o.isParked && !freeformKeySet.has(keyForOrder(o)) && !lineItemKeySet.has(keyForOrder(o)));
      const incomingKeys = visibleOrders.map((o) => keyForOrder(o));
      const justUnparkedKeys = visibleOrders
        .map((o) => {
          const key = keyForOrder(o);
          return previousParkStateRef.current.get(key) === true ? key : null;
        })
        .filter(Boolean);
      const justUnparkedSet = new Set(justUnparkedKeys);
      const fireUnparkedSet = new Set(
        lastUnparkSignal?.source === "fire-local"
          ? justUnparkedKeys.filter((key) => {
              const order = orderByKey[key];
              const sameTicket =
                lastUnparkSignal.ticket_instance_id &&
                String(order?.ticket_instance_id || "").split("-")[0] ===
                  String(lastUnparkSignal.ticket_instance_id).split("-")[0];
              const sameOrder =
                lastUnparkSignal.order_id != null &&
                String(order?.order_id ?? "") === String(lastUnparkSignal.order_id);
              return sameTicket || sameOrder;
            })
          : []
      );
      const frontUnparkedKeys = justUnparkedKeys.filter((key) => !fireUnparkedSet.has(key));
      const endUnparkedKeys = justUnparkedKeys.filter((key) => fireUnparkedSet.has(key));

      const stillThere = prev.map((pg) => pg.filter((k) => incomingKeys.includes(k)));
      const newOnes = incomingKeys.filter((k) => !known.has(k));
      const retained = stillThere
        .flat()
        .filter((k) => !justUnparkedSet.has(k));
      const fresh = newOnes.filter((k) => !justUnparkedSet.has(k));
      const flattened = frontUnparkedKeys.concat(retained, fresh, endUnparkedKeys);

      const next = chunkIntoPages(flattened, TICKETS_PER_PAGE);

      if (frontUnparkedKeys.length > 0) {
        setCurrentPage(0);
      }

      if (currentPage >= next.length) {
        setCurrentPage(Math.max(0, next.length - 1));
      }

      return next;
    });

    previousParkStateRef.current = new Map(
      orders.map((order) => [keyForOrder(order), !!order?.isParked])
    );
  }, [orders, currentPage, freeformKeySet, lineItemKeySet, lastUnparkSignal, orderByKey]);

  const pageKeys = pages[currentPage] ?? [];
  const pageCards = pageKeys
    .map((k, i) => ({
      id: k,
      key: k,
      order: orderByKey[k],
      orderIndex: i,
    }))
    .filter((card) => !!card.order);
  const selectableBumpBarPageCards = useMemo(
    () => pageCards.filter((card) => isBumpBarSelectableTicket(card.order)),
    [pageCards]
  );
  const freeformCards = useMemo(() => {
    return Array.from(freeformKeySet)
      .map((key, index) => ({
        id: key,
        key,
        order: orderByKey[key],
        orderIndex: index,
      }))
      .filter((card) => !!card.order);
  }, [freeformKeySet, orderByKey]);
  const freeformCardsForMenu = useMemo(() => {
    return [...freeformCards].sort((left, right) => {
      const leftZ = Number(freeformLayouts[left.key]?.zIndex) || 0;
      const rightZ = Number(freeformLayouts[right.key]?.zIndex) || 0;
      return leftZ - rightZ;
    });
  }, [freeformCards, freeformLayouts]);
  const selectedExposeOrder = useMemo(() => {
    if (!selectedExposeTicketKey) return null;
    return freeformCardsForMenu.find((card) => card.key === selectedExposeTicketKey)?.order || null;
  }, [freeformCardsForMenu, selectedExposeTicketKey]);
  const visibleTicketCount = Math.min(
    TICKETS_PER_PAGE,
    Math.max(1, pageCards.length)
  );
  const ticketWidthSlotCount = Math.max(4, visibleTicketCount);
  const ticketGapPx = 4;
  const rightScrollGutterPx = 50;

  const [isPagePickerOpen, setIsPagePickerOpen] = useState(false);
  const [pendingDropKey, setPendingDropKey] = useState(null);
  const defaultOrderDestination = settings?.default_order_destination || "normal";

  const getStableTicketRef = useCallback((order) => {
    const card = Number.isFinite(Number(order?.card)) ? Number(order.card) : 0;
    if (order?.order_id != null) {
      return `${String(order.order_id).trim()}::${card}`;
    }
    if (order?.ticket_instance_id) {
      return `${String(order.ticket_instance_id).split("-")[0].trim()}::${card}`;
    }
    return null;
  }, []);

  const buildSyncedPageOrder = useCallback((remoteOrderedRefs = []) => {
    const visibleOrders = orders.filter((order) => !order?.isParked && !freeformKeySet.has(keyForOrder(order)));
    const keyByRef = new Map();
    const visibleKeys = visibleOrders.map((order) => {
      const key = keyForOrder(order);
      const stableRef = getStableTicketRef(order);
      if (stableRef) {
        keyByRef.set(stableRef, key);
      }
      return key;
    });

    const prioritized = (Array.isArray(remoteOrderedRefs) ? remoteOrderedRefs : [])
      .map((value) => String(value || "").trim())
      .map((stableRef) => keyByRef.get(stableRef) || null)
      .filter(Boolean);
    const prioritizedSet = new Set(prioritized);
    const remainder = visibleKeys.filter((key) => !prioritizedSet.has(key));
    return chunkIntoPages(prioritized.concat(remainder), TICKETS_PER_PAGE);
  }, [freeformKeySet, getStableTicketRef, orders]);

  const shouldApplyRemoteTicketLocationEvent = useCallback((data = {}) => {
    const mirroredStationIds = Array.isArray(data?.mirrored_station_ids)
      ? data.mirrored_station_ids
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value))
      : [];
    if (mirroredStationIds.length > 0) {
      return mirroredStationIds.includes(Number(stationId));
    }

    const method = String(settings?.ticket_location_sync_method || "none")
      .trim()
      .toLowerCase();
    if (method === "all_stations") return true;
    if (
      method === "expo" &&
      String(data?.sender_role || "").trim().toLowerCase() === "expo"
    ) {
      return true;
    }
    return false;
  }, [settings?.ticket_location_sync_method, stationId]);

  const shouldApplyRemoteTicketPageEvent = useCallback((data = {}) => {
    const mirroredStationIds = Array.isArray(data?.mirrored_station_ids)
      ? data.mirrored_station_ids
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value))
      : [];
    if (mirroredStationIds.length > 0) {
      return mirroredStationIds.includes(Number(stationId));
    }

    const method = String(settings?.ticket_page_sync_method || "none")
      .trim()
      .toLowerCase();
    if (method === "all_stations") return true;
    if (
      method === "expo" &&
      String(data?.sender_role || "").trim().toLowerCase() === "expo"
    ) {
      return true;
    }
    return false;
  }, [settings?.ticket_page_sync_method, stationId]);

  const goToPage = useCallback((nextPage, { sync = false } = {}) => {
    const totalPageCount = Math.max(1, pages.length);
    const safeTarget = Math.max(0, Math.min(Number(nextPage) || 0, totalPageCount - 1));
    setCurrentPage(safeTarget);
    if (sync && safeTarget !== currentPageRef.current) {
      emitTicketPageSync({ pageIndex: safeTarget });
    }
  }, [emitTicketPageSync, pages.length]);
  const goToDisplayPage = useCallback((nextPage, { sync = false } = {}) => {
    const specialCount = Number(freeformEnabled) + Number(lineItemBoardEnabled);
    if (!specialCount) {
      goToPage(nextPage, { sync });
      return;
    }
    const totalDisplayPages = Math.max(1, pages.length + specialCount);
    const safeTarget = Math.max(0, Math.min(Number(nextPage) || 0, totalDisplayPages - 1));
    if (freeformEnabled && safeTarget === 0) {
      setIsViewingFreeform(true);
      setIsViewingLineItems(false);
      return;
    }
    if (lineItemBoardEnabled && safeTarget === Number(freeformEnabled)) {
      setIsViewingFreeform(false);
      setIsViewingLineItems(true);
      return;
    }
    setIsViewingFreeform(false);
    setIsViewingLineItems(false);
    goToPage(safeTarget - specialCount, { sync });
  }, [freeformEnabled, lineItemBoardEnabled, goToPage, pages.length]);

  const getBumpBarVisibleItems = useCallback((order) => {
    const allItems = Array.isArray(order?.items) ? order.items : [];
    if (!settings?.coursing) return allItems;

    const bumpBarSettings = {
      ...settings,
      hide_bumped_courses: false,
    };
    const fireItems = allItems.filter((item) => item?.isFireItem);
    const grouped = new Map();

    for (const item of allItems) {
      if (!grouped.has(item?.course)) grouped.set(item?.course, []);
      grouped.get(item?.course).push(item);
    }

    const visible = [];
    const courseIndices = [...grouped.keys()].sort((a, b) => Number(a) - Number(b));

    for (let i = 0; i < courseIndices.length; i += 1) {
      const course = courseIndices[i];
      const courseItems = grouped.get(course) || [];
      const prevCoursesBumped = courseIndices.slice(0, i).every((prev) => {
        const prevItems = grouped.get(prev) || [];
        const prevReal = prevItems.filter((item) => !item?.isCourseSeparator);
        return prevReal.length === 0
          ? true
          : prevReal.every((item) => item?.isBumped || item?.isCourseSeparator);
      });
      const unbumpedReal = courseItems.filter(
        (item) => !item?.isCourseSeparator && !item?.isBumped
      );
      const onlySeparatorLeft = unbumpedReal.length === 0;

      if (bumpBarSettings.hide_course && (!prevCoursesBumped || onlySeparatorLeft)) {
        continue;
      }

      visible.push(...courseItems);
    }

    return bumpBarSettings.use_course_separator ? [...fireItems, ...visible] : [...fireItems, ...visible];
  }, [settings]);

  const getBumpBarSelectableItems = useCallback(
    (order) => getBumpBarVisibleItems(order).filter(isBumpBarRealItem),
    [getBumpBarVisibleItems]
  );

  const getSelectedBumpBarOrder = useCallback(() => {
    if (!bumpBarSelectedTicketKey) return null;
    return orderByKey[bumpBarSelectedTicketKey] || null;
  }, [bumpBarSelectedTicketKey, orderByKey]);

  const clearBumpBarSelection = useCallback(() => {
    setBumpBarSelectionMode("ticket");
    setBumpBarSelectedTicketKey(null);
    setBumpBarSelectedItemId(null);
    setBumpBarSelectedPageControl(null);
  }, []);

  const selectBumpBarTicketBySlot = useCallback((slotIndex) => {
    const card = pageCards[slotIndex];
    if (!card || !isBumpBarSelectableTicket(card.order)) return;
    setBumpBarSelectionMode("ticket");
    setBumpBarSelectedTicketKey(card.key);
    setBumpBarSelectedItemId(null);
    setBumpBarSelectedPageControl(null);
  }, [pageCards]);

  const moveBumpBarTicketSelection = useCallback((direction) => {
    const pageControls = [
      ...(activePageIndex > 0 ? [{ kind: "page", control: "previous" }] : []),
      ...(activePageIndex < totalPages - 1 ? [{ kind: "page", control: "next" }] : []),
    ];
    const candidates = [
      ...pageControls.filter((control) => control.control === "previous"),
      ...selectableBumpBarPageCards.map((card) => ({ kind: "ticket", card })),
      ...pageControls.filter((control) => control.control === "next"),
    ];

    if (!candidates.length) {
      clearBumpBarSelection();
      return;
    }

    const currentIndex = candidates.findIndex((candidate) => {
      if (candidate.kind === "page") {
        return bumpBarSelectedPageControl === candidate.control;
      }
      return !bumpBarSelectedPageControl && candidate.card.key === bumpBarSelectedTicketKey;
    });
    const fallbackIndex = direction < 0 ? candidates.length - 1 : 0;
    const nextIndex =
      currentIndex === -1
        ? fallbackIndex
        : (currentIndex + direction + candidates.length) % candidates.length;
    const nextCandidate = candidates[nextIndex];

    setBumpBarSelectionMode("ticket");
    setBumpBarSelectedItemId(null);
    if (nextCandidate.kind === "page") {
      setBumpBarSelectedTicketKey(null);
      setBumpBarSelectedPageControl(nextCandidate.control);
      return;
    }

    setBumpBarSelectedTicketKey(nextCandidate.card.key);
    setBumpBarSelectedPageControl(null);
  }, [
    activePageIndex,
    bumpBarSelectedTicketKey,
    bumpBarSelectedPageControl,
    clearBumpBarSelection,
    selectableBumpBarPageCards,
    totalPages,
  ]);

  const selectBumpBarItemBySlot = useCallback((slotIndex) => {
    const order = getSelectedBumpBarOrder();
    const items = getBumpBarSelectableItems(order);
    const item = items[slotIndex];
    if (!item) return;
    setBumpBarSelectedItemId(item.ID);
  }, [getBumpBarSelectableItems, getSelectedBumpBarOrder]);

  const moveBumpBarItemSelection = useCallback((direction) => {
    const order = getSelectedBumpBarOrder();
    const items = getBumpBarSelectableItems(order);
    if (!items.length) {
      setBumpBarSelectedItemId(null);
      return;
    }

    const currentIndex = items.findIndex(
      (item) => String(item.ID) === String(bumpBarSelectedItemId)
    );
    const fallbackIndex = direction < 0 ? items.length - 1 : 0;
    const nextIndex =
      currentIndex === -1
        ? fallbackIndex
        : (currentIndex + direction + items.length) % items.length;

    setBumpBarSelectedItemId(items[nextIndex].ID);
  }, [
    bumpBarSelectedItemId,
    getBumpBarSelectableItems,
    getSelectedBumpBarOrder,
  ]);

  const activateBumpBarSelectedPageControl = useCallback(() => {
    if (!bumpBarSelectedPageControl) return false;
    const direction = bumpBarSelectedPageControl === "previous" ? -1 : 1;
    const nextDisplayPage = activePageIndex + direction;
    if (nextDisplayPage < 0 || nextDisplayPage >= totalPages) return true;

    const selectedSlot = Math.max(
      0,
      pageCards.findIndex((card) => card.key === bumpBarSelectedTicketKey)
    );
    const specialCount = Number(freeformEnabled) + Number(lineItemBoardEnabled);
    const nextNormalPage = nextDisplayPage - specialCount;
    const nextPageKeys = nextNormalPage >= 0 ? pages[nextNormalPage] || [] : [];
    const nextKey = nextPageKeys[selectedSlot] || nextPageKeys[0] || null;
    const nextOrder = nextKey ? orderByKey[nextKey] : null;

    goToDisplayPage(nextDisplayPage, { sync: true });

    if (nextOrder && isBumpBarSelectableTicket(nextOrder)) {
      setBumpBarSelectionMode("ticket");
      setBumpBarSelectedTicketKey(nextKey);
      setBumpBarSelectedItemId(null);
      setBumpBarSelectedPageControl(null);
    } else {
      clearBumpBarSelection();
    }

    return true;
  }, [
    activePageIndex,
    bumpBarSelectedTicketKey,
    bumpBarSelectedPageControl,
    clearBumpBarSelection,
    freeformEnabled,
    goToDisplayPage,
    lineItemBoardEnabled,
    orderByKey,
    pageCards,
    pages,
    totalPages,
  ]);

  const getSortModeLabel = useCallback((mode) => {
    switch (mode) {
      case "rows-by-oldest":
        return "Rows By Oldest First";
      case "rows-by-newest":
        return "Rows By Newest First";
      case "rows-fired-first":
        return "Rows With Fired Tickets First";
      case "stack-by-oldest":
        return "Diagonal Column By Oldest First";
      case "stack-by-newest":
        return "Diagonal Column By Newest First";
      case "column-by-oldest":
        return "Column By Oldest First";
      case "column-by-newest":
        return "Column By Newest First";
      case "column-fired-first":
        return "Column With Fired Tickets First";
      case "stack-by-most-items":
        return "Diagonal Column By Most Items First";
      case "stack-by-least-items":
        return "Diagonal Column By Least Items First";
      case "column-by-most-items":
        return "Column By Most Items First";
      case "column-by-least-items":
        return "Column By Least Items First";
      case "course-columns":
        return "Columns By Current Course";
      case "rows-by-current-course":
        return "Rows By Current Course";
      case "rows-by-most-items":
        return "Rows By Most Items First";
      case "rows-by-least-items":
        return "Rows By Least Items First";
      case "compact-pack":
        return "Compact Pack";
      case "dense-grid":
        return "Dense Grid";
      default:
        return "Rows By Oldest First";
    }
  }, []);

  useEffect(() => {
    if (freeformEnabled) {
      setIsViewingFreeform(true);
      setIsViewingLineItems(false);
    } else if (lineItemBoardEnabled) {
      setIsViewingFreeform(false);
      setIsViewingLineItems(true);
    } else {
      setIsViewingFreeform(false);
      setIsViewingLineItems(false);
    }
  }, [freeformEnabled, lineItemBoardEnabled]);

  useEffect(() => {
    if (!lineItemBoardEnabled) setIsViewingLineItems(false);
  }, [lineItemBoardEnabled]);

  const moveKeyToGlobalIndex = (key, targetGlobalIndex) => {
    setPages((prev) => {
      const keys = prev.flat();
      const fromIdx = keys.indexOf(key);
      if (fromIdx === -1) return prev;

      keys.splice(fromIdx, 1);
      const clamped = Math.max(0, Math.min(targetGlobalIndex, keys.length));
      keys.splice(clamped, 0, key);

      const next = chunkIntoPages(keys, TICKETS_PER_PAGE);
      if (currentPage >= next.length) setCurrentPage(Math.max(0, next.length - 1));
      return next;
    });
  };

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const handleRemoteTicketLocationSync = (data = {}) => {
      if (data?.sender_screen_id === screenMountId) return;
      if (!shouldApplyRemoteTicketLocationEvent(data)) return;

      const remoteOrderedRefs = Array.isArray(data?.ordered_refs)
        ? data.ordered_refs
        : Array.isArray(data?.ordered_keys)
          ? data.ordered_keys
        : [];
      if (!remoteOrderedRefs.length) return;

      const nextPages = buildSyncedPageOrder(remoteOrderedRefs);
      const targetGlobalIndex = Number(data?.target_global_index);
      const nextPageIndex = Number.isFinite(targetGlobalIndex)
        ? Math.max(0, Math.floor(targetGlobalIndex / TICKETS_PER_PAGE))
        : currentPage;

      setPages(nextPages);
      setCurrentPage(Math.min(nextPageIndex, Math.max(0, nextPages.length - 1)));
    };

    socket.on("ticket-location-sync", handleRemoteTicketLocationSync);
    return () => {
      socket.off("ticket-location-sync", handleRemoteTicketLocationSync);
    };
  }, [buildSyncedPageOrder, currentPage, screenMountId, shouldApplyRemoteTicketLocationEvent]);

  useEffect(() => {
    const handleRemoteTicketPageSync = (data = {}) => {
      if (data?.sender_screen_id === screenMountId) return;
      if (!shouldApplyRemoteTicketPageEvent(data)) return;

      const requestedPage = Number(data?.page_index);
      if (!Number.isFinite(requestedPage)) return;

      const totalPageCount = Math.max(1, pages.length);
      setCurrentPage(Math.max(0, Math.min(requestedPage, totalPageCount - 1)));
    };

    socket.on("ticket-page-sync", handleRemoteTicketPageSync);
    return () => {
      socket.off("ticket-page-sync", handleRemoteTicketPageSync);
    };
  }, [pages.length, screenMountId, shouldApplyRemoteTicketPageEvent]);

  useEffect(() => {
    if (!lastUnparkSignal?.nonce) return;
    if (handledUnparkPromotionNonceRef.current === lastUnparkSignal.nonce) return;

    const normalizedTicket = lastUnparkSignal.ticket_instance_id
      ? String(lastUnparkSignal.ticket_instance_id).split("-")[0]
      : null;
    const matchingOrder = orders.find((order) => {
      if (order?.isParked) return false;
      const sameTicket =
        normalizedTicket &&
        String(order?.ticket_instance_id || "").split("-")[0] === normalizedTicket;
      const sameOrder =
        lastUnparkSignal.order_id != null &&
        String(order?.order_id ?? "") === String(lastUnparkSignal.order_id);
      return sameTicket || sameOrder;
    });

    if (!matchingOrder) return;

    const promotionKey = keyForOrder(matchingOrder);
    handledUnparkPromotionNonceRef.current = lastUnparkSignal.nonce;
    const shouldAppend = lastUnparkSignal.source === "fire-local";
    const targetIndex = shouldAppend ? pages.flat().length : 0;
    moveKeyToGlobalIndex(promotionKey, targetIndex);
    if (!shouldAppend) {
      setCurrentPage(0);
    }
  }, [lastUnparkSignal, orders, pages]);

  const persistFreeformLayout = useCallback(async ({
    key,
    stationId: targetStationId,
    ticketInstanceId,
    orderId,
    card,
    x,
    y,
    zIndex,
    scale = 1,
  }) => {
    const nextLayout = { ticketInstanceId, orderId, card, x, y, zIndex, scale };
    setFreeformLayouts((prev) => ({ ...prev, [key]: nextLayout }));
    try {
      await apiFetch(`/freeform-board/layouts/${targetStationId}`, {
        method: "PUT",
        body: JSON.stringify({
          ticketInstanceId,
          orderId,
          card,
          x,
          y,
          zIndex,
          scale,
        }),
      });
    } catch (err) {
      console.error("Failed to persist freeform layout:", err);
    }
  }, []);

  const deleteFreeformLayouts = useCallback(async ({
    ticketInstanceId = null,
    orderId = null,
    card = null,
  }) => {
    if (!stationId) return;
    const params = new URLSearchParams();
    if (ticketInstanceId) params.set("ticketInstanceId", ticketInstanceId);
    if (orderId != null && orderId !== "") params.set("orderId", String(orderId));
    if (card != null && Number.isFinite(Number(card))) params.set("card", String(Number(card)));
    const query = params.toString();
    try {
      await apiFetch(`/freeform-board/layouts/${stationId}${query ? `?${query}` : ""}`, {
        method: "DELETE",
      });
    } catch (err) {
      console.error("Failed to delete freeform layout:", err);
    }
  }, [stationId]);

  const bringFreeformToFront = useCallback((key) => {
    let nextZ = 1;
    setFreeformLayouts((prev) => {
      nextZ = Object.values(prev).reduce(
        (max, layout) => Math.max(max, Number(layout?.zIndex) || 0),
        0
      ) + 1;
      return {
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          zIndex: nextZ,
        },
      };
    });
    return nextZ;
  }, []);
  const exposeFreeformTicket = useCallback(async (key) => {
    const order = orderByKey[key];
    if (!order) return;
    const existing = freeformLayouts[key] || {};
    const nextZ = bringFreeformToFront(key);
    await persistFreeformLayout({
      key,
      stationId,
      ticketInstanceId: order.ticket_instance_id,
      orderId: order.order_id ?? null,
      card: Number(order.card) || 0,
      x: Number(existing?.x) || FREEFORM_SPAWN_X,
      y: Number(existing?.y) || FREEFORM_SPAWN_Y,
      zIndex: nextZ,
      scale: Number(existing?.scale) || 1,
    });
  }, [bringFreeformToFront, freeformLayouts, orderByKey, persistFreeformLayout, stationId]);

  const moveTicketToFreeform = useCallback(async (key) => {
    const order = orderByKey[key];
    if (!order) return;
    const existing = freeformLayouts[key];
    const nextZ = Object.values(freeformLayouts).reduce(
      (max, layout) => Math.max(max, Number(layout?.zIndex) || 0),
      0
    ) + 1;
    await persistFreeformLayout({
      key,
      stationId,
      ticketInstanceId: order.ticket_instance_id,
      orderId: order.order_id ?? null,
      card: Number(order.card) || 0,
      x: FREEFORM_SPAWN_X,
      y: FREEFORM_SPAWN_Y,
      zIndex: Number(existing?.zIndex) || nextZ,
      scale: Number(existing?.scale) || 1,
    });
    setPages((prev) => prev.map((page) => page.filter((pageKey) => pageKey !== key)));
  }, [freeformLayouts, orderByKey, persistFreeformLayout, stationId]);

  const moveTicketToLineItemBoard = useCallback((key) => {
    setLineItemTicketKeys((previous) => new Set([...previous, key]));
    setPages((previous) => previous.map((page) => page.filter((pageKey) => pageKey !== key)));
  }, []);

  useEffect(() => {
    const currentVisibleKeys = new Set(
      orders
        .filter((order) => !order?.isParked)
        .map((order) => keyForOrder(order))
        .filter(Boolean)
    );

    const previousVisibleKeys = autoMoveSeenTicketKeysRef.current;
    autoMoveSeenTicketKeysRef.current = currentVisibleKeys;

    if (!(previousVisibleKeys instanceof Set)) {
      return;
    }

    if (defaultOrderDestination === "normal") {
      return;
    }

    const newlyArrivedKeys = Array.from(currentVisibleKeys).filter(
      (key) => !previousVisibleKeys.has(key)
    );

    if (!newlyArrivedKeys.length) {
      return;
    }

    const moveableNewKeys = newlyArrivedKeys.filter((key) => {
      if (freeformKeySet.has(key) || lineItemKeySet.has(key)) return false;
      const order = orderByKey[key];
      return !!order && !order?.isParked;
    });

    if (!moveableNewKeys.length) {
      return;
    }

    moveableNewKeys.forEach((key) => {
      if (defaultOrderDestination === "freeform" && freeformEnabled) moveTicketToFreeform(key);
      if (defaultOrderDestination === "line_items" && lineItemBoardEnabled) moveTicketToLineItemBoard(key);
    });
  }, [
    defaultOrderDestination,
    freeformKeySet,
    lineItemKeySet,
    lineItemBoardEnabled,
    freeformEnabled,
    moveTicketToFreeform,
    moveTicketToLineItemBoard,
    orderByKey,
    orders,
  ]);

  const pinTicketToNormalPages = useCallback(async (key) => {
    const order = orderByKey[key];
    if (!order) return;

    setFreeformLayouts((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

    await deleteFreeformLayouts({
      ticketInstanceId: order.ticket_instance_id,
      orderId: order.order_id ?? null,
      card: Number(order.card) || 0,
    });

    const currentVisibleKeys = pages.flat().filter((pageKey) => pageKey !== key);
    const nextVisibleKeys = [...currentVisibleKeys, key];
    const targetGlobalIndex = Math.max(0, nextVisibleKeys.length - 1);

    moveKeyToGlobalIndex(key, targetGlobalIndex);
    emitTicketLocationSync({
      order,
      ticketKey: key,
      orderedKeys: nextVisibleKeys,
      targetGlobalIndex,
    });
  }, [deleteFreeformLayouts, emitTicketLocationSync, moveKeyToGlobalIndex, orderByKey, pages]);

  const handlePickPage = (targetPageIndex) => {
    if (!pendingDropKey) return;
    if (targetPageIndex === "freeform") {
      moveTicketToFreeform(pendingDropKey);
      setIsPagePickerOpen(false);
      setPendingDropKey(null);
      return;
    }
    if (targetPageIndex === "line_items") {
      moveTicketToLineItemBoard(pendingDropKey);
      setIsPagePickerOpen(false);
      setPendingDropKey(null);
      return;
    }
    const targetGlobalIndex =
      targetPageIndex * TICKETS_PER_PAGE + (TICKETS_PER_PAGE - 1);
    const currentVisibleKeys = pages.flat();
    const fromIndex = currentVisibleKeys.indexOf(pendingDropKey);
    const nextVisibleKeys = [...currentVisibleKeys];
    if (fromIndex !== -1) {
      nextVisibleKeys.splice(fromIndex, 1);
    }
    const clampedIndex = Math.max(0, Math.min(targetGlobalIndex, nextVisibleKeys.length));
    nextVisibleKeys.splice(clampedIndex, 0, pendingDropKey);

    moveKeyToGlobalIndex(pendingDropKey, targetGlobalIndex);
    emitTicketLocationSync({
      order: orderByKey[pendingDropKey] || null,
      ticketKey: pendingDropKey,
      orderedKeys: nextVisibleKeys,
      targetGlobalIndex: clampedIndex,
    });
    setIsPagePickerOpen(false);
    setPendingDropKey(null);
  };

  const handleBoardReorder = useCallback(({ nextPageKeys, changedKey }) => {
    const currentPageKeys = pages[currentPage] ?? [];
    if (!Array.isArray(nextPageKeys) || nextPageKeys.length !== currentPageKeys.length) {
      return;
    }

    const nextPages = pages.map((p, index) =>
      index === currentPage ? nextPageKeys : [...p]
    );

    setPages(nextPages);

    if (changedKey) {
      const orderedKeys = nextPages.flat();
      emitTicketLocationSync({
        order: orderByKey[changedKey] || null,
        ticketKey: changedKey,
        orderedKeys,
        targetGlobalIndex: orderedKeys.indexOf(changedKey),
      });
    }
  }, [currentPage, emitTicketLocationSync, orderByKey, pages]);

  const openPagePickerForKey = useCallback((ticketKey) => {
    if (!ticketKey) return;
    setPendingDropKey(ticketKey);
    setIsPagePickerOpen(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadFreeformLayouts() {
      if (!stationId || !freeformEnabled) {
        if (!cancelled) setFreeformLayouts({});
        return;
      }
      try {
        const res = await apiFetch(`/freeform-board/layouts/${stationId}`);
        if (!res.ok) throw new Error(`Failed to load freeform layouts (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        const nextLayouts = {};
        (data?.layouts || []).forEach((layout) => {
          nextLayouts[keyForLayout(layout.ticketInstanceId, layout.card)] = {
            ticketInstanceId: layout.ticketInstanceId,
            orderId: layout.orderId ?? null,
            card: Number(layout.card) || 0,
            x: Number(layout.x) || 0,
            y: Number(layout.y) || 0,
            zIndex: Number(layout.zIndex) || 0,
            scale: Number(layout.scale) || 1,
          };
        });
        setFreeformLayouts(nextLayouts);
      } catch (err) {
        console.error("Failed to load freeform layouts:", err);
        if (!cancelled) setFreeformLayouts({});
      }
    }
    loadFreeformLayouts();
    return () => {
      cancelled = true;
    };
  }, [freeformEnabled, keyForLayout, stationId]);

  useEffect(() => {
    let cancelled = false;
    async function loadLineItemOrdering() {
      if (!stationId || !lineItemBoardEnabled) return;
      try {
        const res = await apiFetch(`/line-item-board/ordering/${stationId}`);
        if (!res.ok) throw new Error(`Failed to load line item ordering (${res.status})`);
        const data = await res.json();
        if (!cancelled && Array.isArray(data?.itemOrder)) {
          setLineItemRowOrder(data.itemOrder);
        }
      } catch (err) {
        console.error("Failed to load line item board ordering:", err);
      }
    }
    loadLineItemOrdering();
    return () => {
      cancelled = true;
    };
  }, [lineItemBoardEnabled, stationId]);

  const [soundSettings, setSoundSettings] = useState({
    newTicket: {
      sound: "",
      durationMs: 1500
    },
    ticketYellow: { sound: "", durationMs: 1500 },
    ticketRed: { sound: "", durationMs: 1500 },
    addonReceived: {
      sound: "",
      durationMs: 1500
    },
    voidedItem: {
      sound: "",
      durationMs: 1500
    },
    courseFired: {
      sound: "",
      durationMs: 1500
    },
    courseBumped: {
      sound: "",
      durationMs: 1500
    },
    ticketBumped: {
      sound: "",
      durationMs: 1500
    }
  });
  const [soundOptions, setSoundOptions] = useState([]);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const [soundSettingsLoaded, setSoundSettingsLoaded] = useState(false);
  const audioRef = useRef(null);
  const timeoutRef = useRef(null);
  const playedNewTicketSoundKeysRef = useRef(new Map());
  const playedVoidedItemSoundKeysRef = useRef(new Map());
  const playedCourseFiredSoundKeysRef = useRef(new Map());
  const ticketColorStateRef = useRef(new Map());
  const ticketColorDiagnosticRef = useRef(new Map());
  const ticketColorSoundOrdersRef = useRef([]);
  const ticketColorSoundSettingsRef = useRef({});
  const ticketColorSoundInitializedRef = useRef(false);
  const audioUnlockedRef = useRef(false);
  const pendingSoundEventRef = useRef(null);
  const soundOptionsRef = useRef(soundOptions);
  const soundSettingsRef = useRef(soundSettings);
  const soundSettingsLoadedRef = useRef(soundSettingsLoaded);
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  const buildSoundSrc = sound => {
    const opt = soundOptionsRef.current.find(o => o.file === sound);
    const base = API_BASE.replace(/\/$/, "");
    const pathPiece = opt?.type === "system" ? "/sounds" : "/uploads/sounds";
    return `${base}${pathPiece}/${encodeURIComponent(sound)}`;
  };
  const playEventSound = (eventKey, options = {}) => {
    const {
      fromUnlock = false
    } = options;
    const currentSoundSettings = soundSettingsRef.current || {};
    const cfg = currentSoundSettings[eventKey];
    if (!soundSettingsLoadedRef.current) {
      pendingSoundEventRef.current = eventKey;
      return;
    }
    if (!cfg || !cfg.sound) {
      return;
    }
    if (!audioUnlockedRef.current && !fromUnlock) {
      pendingSoundEventRef.current = eventKey;
      return;
    }
    const {
      sound,
      durationMs
    } = cfg;
    try {
      stopAudio();
      const src = buildSoundSrc(sound);
      const audio = new Audio(src);
      audioRef.current = audio;
      if (durationMs && durationMs > 0) {
        audio.loop = true;
        timeoutRef.current = window.setTimeout(() => {
          if (audioRef.current === audio) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
          }
          timeoutRef.current = null;
        }, durationMs);
        audio.onended = null;
      } else {
        audio.loop = false;
        audio.onended = () => {
          if (audioRef.current === audio) audioRef.current = null;
        };
      }
      audio.play().catch(err => {
        const blockedByAutoplay = err?.name === "NotAllowedError" || /user gesture|interact|allowed/i.test(String(err?.message || ""));
        if (blockedByAutoplay) {
          pendingSoundEventRef.current = eventKey;
          audioUnlockedRef.current = false;
          setIsAudioUnlocked(false);
        }
        console.error(`Failed to play "${eventKey}" sound:`, err);
      });
    } catch (err) {
      console.error(`Error setting up "${eventKey}" sound:`, err);
    }
  };
  useEffect(() => {
    soundOptionsRef.current = soundOptions;
  }, [soundOptions]);
  useEffect(() => {
    soundSettingsRef.current = soundSettings;
  }, [soundSettings]);
  useEffect(() => {
    soundSettingsLoadedRef.current = soundSettingsLoaded;
  }, [soundSettingsLoaded]);
  useEffect(() => () => stopAudio(), []);
  useEffect(() => {
    const unlockAudio = event => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      setIsAudioUnlocked(true);
      const pendingEvent = pendingSoundEventRef.current;
      pendingSoundEventRef.current = null;
      if (pendingEvent) {
        window.setTimeout(() => {
          playEventSound(pendingEvent, {
            fromUnlock: true
          });
        }, 0);
      }
    };
    document.addEventListener("pointerdown", unlockAudio, {
      passive: true
    });
    document.addEventListener("keydown", unlockAudio, {
      passive: true
    });
    return () => {
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };
  }, [soundOptions, soundSettings, soundSettingsLoaded]);
  const normalizeSoundTicketKey = (payload = {}) => {
    const ticketKey = String(payload?.ticket_instance_id || "").trim();
    if (ticketKey) return `ticket:${ticketKey}`;
    const orderKey = payload?.order_id ?? payload?.ID ?? null;
    return orderKey != null ? `order:${String(orderKey).trim()}` : null;
  };
  const normalizeVoidedItemSoundKey = (payload = {}) => {
    const ticketKey = String(payload?.ticket_instance_id || "").trim();
    const itemKey = String(payload?.item_id || payload?.ID || "").trim();
    if (ticketKey && itemKey) return `ticket:${ticketKey}:item:${itemKey}`;
    const orderKey = payload?.order_id ?? payload?.ID ?? null;
    if (orderKey != null && itemKey) return `order:${String(orderKey).trim()}:item:${itemKey}`;
    return itemKey ? `item:${itemKey}` : null;
  };
  const playNewTicketSoundOnce = (payload = {}) => {
    const key = normalizeSoundTicketKey(payload);
    if (!key) {
      playEventSound("newTicket");
      return;
    }
    const now = Date.now();
    const seenAt = Number(playedNewTicketSoundKeysRef.current.get(key) || 0);
    if (now - seenAt < 5000) {
      return;
    }
    playedNewTicketSoundKeysRef.current.set(key, now);
    if (playedNewTicketSoundKeysRef.current.size > 100) {
      const cutoff = now - 60000;
      for (const [entryKey, entryTime] of playedNewTicketSoundKeysRef.current.entries()) {
        if (Number(entryTime) < cutoff) {
          playedNewTicketSoundKeysRef.current.delete(entryKey);
        }
      }
    }
    playEventSound("newTicket");
  };
  const playVoidedItemSoundOnce = (payload = {}) => {
    const key = normalizeVoidedItemSoundKey(payload);
    if (!key) {
      playEventSound("voidedItem");
      return;
    }
    const now = Date.now();
    const seenAt = Number(playedVoidedItemSoundKeysRef.current.get(key) || 0);
    if (now - seenAt < 1500) {
      return;
    }
    playedVoidedItemSoundKeysRef.current.set(key, now);
    if (playedVoidedItemSoundKeysRef.current.size > 100) {
      const cutoff = now - 60000;
      for (const [entryKey, entryTime] of playedVoidedItemSoundKeysRef.current.entries()) {
        if (Number(entryTime) < cutoff) {
          playedVoidedItemSoundKeysRef.current.delete(entryKey);
        }
      }
    }
    playEventSound("voidedItem");
  };
  const playCourseFiredSoundOnce = (payload = {}) => {
    const ticketKey = String(payload?.ticket_instance_id || "").trim();
    const orderKey = payload?.order_id != null ? String(payload.order_id).trim() : "";
    const courseKey = Number.isFinite(Number(payload?.course)) ? String(Number(payload.course)) : "unknown";
    const key = ticketKey ? `ticket:${ticketKey}:course:${courseKey}` : orderKey ? `order:${orderKey}:course:${courseKey}` : null;
    if (!key) {
      playEventSound("courseFired");
      return;
    }
    const now = Date.now();
    const seenAt = Number(playedCourseFiredSoundKeysRef.current.get(key) || 0);
    if (now - seenAt < 1500) {
      return;
    }
    playedCourseFiredSoundKeysRef.current.set(key, now);
    if (playedCourseFiredSoundKeysRef.current.size > 100) {
      const cutoff = now - 60000;
      for (const [entryKey, entryTime] of playedCourseFiredSoundKeysRef.current.entries()) {
        if (Number(entryTime) < cutoff) {
          playedCourseFiredSoundKeysRef.current.delete(entryKey);
        }
      }
    }
    playEventSound("courseFired");
  };
  const normalizeTicketRoot = value => String(value || "").split("-")[0].trim();
  const normalizeItemRoot = value => String(value || "").split("-")[0].trim();
  const isAddonFlag = value => value === true || value === 1 || value === "1" || String(value || "").toLowerCase() === "true";
  const isUtilityKitchenItem = (item = {}) => {
    const name = String(item?.Name || "").trim();
    const category = String(item?.Category || "").trim();
    const isSeparator = /^\*{3,}.*\*{3,}$/.test(name) || category === "Course Separator";
    const isMessage = name === "* MESSAGE *";
    const isFireCategoryControl = !!String(category).match(/^FIRE\b/i);
    const isFireLine = name === "------ FIRE ------" || (/FIRE/i.test(name) && name !== "* MESSAGE *") || (typeof item?.ID === "string" && item.ID.includes("FIRE"));
    return isSeparator || isMessage || isFireLine || isFireCategoryControl;
  };
  const classifyIncomingTicketSound = useCallback((payload = {}) => {
    const payloadItems = Array.isArray(payload?.Item) ? payload.Item : payload?.Item ? [payload.Item] : [];
    const existingOrder = orders.find(order => {
      const sameOrder = payload?.order_id != null && String(order?.order_id ?? "") === String(payload.order_id);
      const sameLegacyOrder = payload?.ID != null && String(order?.order_id ?? "") === String(payload.ID);
      const sameTicket = payload?.ticket_instance_id && normalizeTicketRoot(order?.ticket_instance_id) === normalizeTicketRoot(payload.ticket_instance_id);
      return sameOrder || sameLegacyOrder || sameTicket;
    });
    if (!existingOrder) {
      return {
        soundKey: "newTicket",
        reason: "brand-new-ticket"
      };
    }
    const existingRealItemIds = new Set((existingOrder?.items || []).filter(item => !isUtilityKitchenItem(item)).map(item => normalizeItemRoot(item?.ID)).filter(Boolean));
    const incomingRealItems = payloadItems.filter(item => !isUtilityKitchenItem(item));
    const introducedItems = incomingRealItems.filter(item => {
      const itemId = normalizeItemRoot(item?.ID);
      return itemId && !existingRealItemIds.has(itemId);
    });
    if (introducedItems.length > 0 && introducedItems.every(item => isAddonFlag(item?.is_addon) || isAddonFlag(item?.isAddon))) {
      return {
        soundKey: "addonReceived",
        reason: "existing-ticket-addon-items",
        introducedItemIds: introducedItems.map(item => item?.ID).filter(Boolean)
      };
    }
    return {
      soundKey: null,
      reason: introducedItems.length > 0 ? "existing-ticket-non-addon-update" : "existing-ticket-no-new-items",
      introducedItemIds: introducedItems.map(item => item?.ID).filter(Boolean)
    };
  }, [orders]);
  useEffect(() => {
    if (!stationId) return;
    let cancelled = false;
    if (!cancelled) setSoundSettingsLoaded(false);
    async function fetchSoundSettings() {
      try {
        const settingsRes = await apiFetch(`/settings/sounds/${stationId}`);
        const data = await settingsRes.json();
        const nextSettings = {
          newTicket: {
            sound: data.new_ticket_sound ?? data.newTicketSound ?? data.newTicket?.sound ?? "",
            durationMs: data.new_ticket_duration_ms ?? data.newTicketDurationMs ?? data.newTicket?.durationMs ?? 1500
          },
          addonReceived: {
            sound: data.addon_received_sound ?? data.addonReceivedSound ?? data.addonReceived?.sound ?? "",
            durationMs: data.addon_received_duration_ms ?? data.addonReceivedDurationMs ?? data.addonReceived?.durationMs ?? 1500
          },
          voidedItem: {
            sound: data.voided_item_sound ?? data.voidedItemSound ?? data.voidedItem?.sound ?? "",
            durationMs: data.voided_item_duration_ms ?? data.voidedItemDurationMs ?? data.voidedItem?.durationMs ?? 1500
          },
          courseFired: {
            sound: data.course_fired_sound ?? data.courseFiredSound ?? data.courseFired?.sound ?? "",
            durationMs: data.course_fired_duration_ms ?? data.courseFiredDurationMs ?? data.courseFired?.durationMs ?? 1500
          },
          ticketYellow: {
            sound:
              data.ticket_yellow_sound ??
              data.ticketYellowSound ??
              data.ticketYellow?.sound ??
              "",
            durationMs:
              data.ticket_yellow_duration_ms ??
              data.ticketYellowDurationMs ??
              data.ticketYellow?.durationMs ??
              1500,
          },
          ticketRed: {
            sound:
              data.ticket_red_sound ??
              data.ticketRedSound ??
              data.ticketRed?.sound ??
              "",
            durationMs:
              data.ticket_red_duration_ms ??
              data.ticketRedDurationMs ??
              data.ticketRed?.durationMs ??
              1500,
          },
          ticketBumped: {
            sound: data.ticket_bumped_sound ?? data.ticketBumpedSound ?? data.ticketBumped?.sound ?? "",
            durationMs: data.ticket_bumped_duration_ms ?? data.ticketBumpedDurationMs ?? data.ticketBumped?.durationMs ?? 1500
          },
          courseBumped: {
            sound: data.course_bumped_sound ?? data.courseBumpedSound ?? data.courseBumped?.sound ?? "",
            durationMs: data.course_bumped_duration_ms ?? data.courseBumpedDurationMs ?? data.courseBumped?.durationMs ?? 1500
          }
        };
        if (!cancelled) {
          setSoundSettings(nextSettings);
        }
        const optionsRes = await apiFetch("/settings/sounds/options");
        const optionsData = await optionsRes.json();
        if (!cancelled) {
          setSoundOptions(optionsData || []);
        }
      } catch (err) {
        console.error("Failed to fetch station sound settings:", err);
      } finally {
        if (!cancelled) {
          setSoundSettingsLoaded(true);
        }
      }
    }
    fetchSoundSettings();
    return () => {
      cancelled = true;
    };
  }, [stationId]);
  useEffect(() => {
    if (!stationId) return;

    let cancelled = false;

    async function fetchPrinters() {
      try {
        const res = await apiFetch("/settings/printers");
        const data = await res.json();
        if (!cancelled) {
          setAvailablePrinters(res.ok && Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch printers for KDS screen:", err);
        if (!cancelled) setAvailablePrinters([]);
      }
    }

    fetchPrinters();
    return () => {
      cancelled = true;
    };
  }, [stationId]);

  const persistLineItemRowOrder = useCallback(async (nextOrder) => {
    const normalizedOrder = Array.isArray(nextOrder) ? nextOrder : [];
    setLineItemRowOrder(normalizedOrder);
    try {
      await apiFetch(`/line-item-board/ordering/${stationId}`, {
        method: "PUT",
        body: JSON.stringify({ itemOrder: normalizedOrder }),
      });
    } catch (err) {
      console.error("Failed to persist line item board ordering:", err);
    }
  }, [stationId]);

  const pinLineItemTicketToNormalPages = useCallback((key) => {
    const order = orderByKey[key];
    if (!order) return;

    setLineItemTicketKeys((previous) => {
      if (!previous.has(key)) return previous;
      const next = new Set(previous);
      next.delete(key);
      return next;
    });

    const rowPrefix = `${order.ticket_instance_id}::`;
    persistLineItemRowOrder(lineItemRowOrder.filter((rowId) => !String(rowId).startsWith(rowPrefix)));
    setIsPeekMode(false);
    setIsPinTicketMode(false);
    setPeekModeIntent(null);
    setCurrentPage(0);
  }, [lineItemRowOrder, orderByKey, persistLineItemRowOrder]);

  const applyLineItemSort = useCallback((mode) => {
    const rows = [...lineItemRowsForSort];
    const ticketCounts = new Map();
    rows.forEach((row) => {
      const key = keyForOrder(row.order);
      ticketCounts.set(key, (ticketCounts.get(key) || 0) + 1);
    });

    const compareWithinTicket = (left, right) => {
      const courseDiff = Number(left?.item?.course ?? 0) - Number(right?.item?.course ?? 0);
      if (courseDiff !== 0) return courseDiff;
      return toSortableTime(left?.item?.createdAt) - toSortableTime(right?.item?.createdAt);
    };

    rows.sort((left, right) => {
      if (mode === "line-fired-time") {
        const leftFired = !!left?.item?.isFired || String(left?.item?.status || "").toLowerCase() === "fired";
        const rightFired = !!right?.item?.isFired || String(right?.item?.status || "").toLowerCase() === "fired";
        if (leftFired !== rightFired) return leftFired ? -1 : 1;
        if (leftFired && rightFired) {
          const firedDiff = toSortableTime(left?.item?.firedAt) - toSortableTime(right?.item?.firedAt);
          if (firedDiff !== 0) return firedDiff;
        }
        return compareLineItemDefault(left, right);
      }

      if (mode === "line-ticket-number") {
        return compareTicketNumber(left, right) || compareWithinTicket(left, right);
      }

      if (mode === "line-course") {
        const courseDiff = Number(left?.item?.course ?? 0) - Number(right?.item?.course ?? 0);
        if (courseDiff !== 0) return courseDiff;
        return compareTicketNumber(left, right) || toSortableTime(left?.item?.createdAt) - toSortableTime(right?.item?.createdAt);
      }

      if (mode === "line-ticket-most-items" || mode === "line-ticket-least-items") {
        const leftKey = keyForOrder(left.order);
        const rightKey = keyForOrder(right.order);
        const countDiff = (ticketCounts.get(leftKey) || 0) - (ticketCounts.get(rightKey) || 0);
        if (countDiff !== 0) {
          return mode === "line-ticket-most-items" ? -countDiff : countDiff;
        }
        return compareTicketNumber(left, right) || compareWithinTicket(left, right);
      }

      return compareLineItemDefault(left, right);
    });

    persistLineItemRowOrder(rows.map((row) => row.id));
    setIsLineItemSortModalOpen(false);
  }, [lineItemRowsForSort, persistLineItemRowOrder]);

  useEffect(() => {
    ticketColorSoundOrdersRef.current = Array.isArray(orders) ? orders : [];
    ticketColorSoundSettingsRef.current = settings || {};
  }, [orders, settings]);

  const syncTicketColorStates = useCallback((shouldPlayTransitions) => {
    const nextStates = new Map();
    const activeOrders = Array.isArray(ticketColorSoundOrdersRef.current)
      ? ticketColorSoundOrdersRef.current
      : [];
    const activeSettings = ticketColorSoundSettingsRef.current || {};

    for (const order of activeOrders) {
      const ticketKey = String(order?.ticket_instance_id || "").trim();
      if (!ticketKey) continue;

      const colorDiagnostic = getCardColorDiagnostic(
        order?.createdAt,
        Array.isArray(order?.items) ? order.items : [],
        activeSettings
      );
      const nextColorState = colorDiagnostic.state;

      nextStates.set(ticketKey, nextColorState);

      // Log one baseline per ticket plus meaningful input/state changes. The
      // elapsed seconds are deliberately excluded from this signature to avoid
      // generating a record every second for every visible ticket.
      const diagnosticSignature = JSON.stringify({
        state: colorDiagnostic.state,
        mode: colorDiagnostic.mode,
        reason: colorDiagnostic.reason,
        createdAt: colorDiagnostic.createdAt,
        createdAtValid: colorDiagnostic.createdAtValid,
        activeCourse: colorDiagnostic.activeCourse,
        previousCourse: colorDiagnostic.previousCourse,
        previousCourseItemCount: colorDiagnostic.previousCourseItemCount,
        previousCourseBumpedCount: colorDiagnostic.previousCourseBumpedCount,
        baseTime: colorDiagnostic.baseTime,
        latestPreviousCourseBumpAt: colorDiagnostic.latestPreviousCourseBumpAt,
        thresholds: colorDiagnostic.thresholds,
        coursing: !!activeSettings.coursing,
        ticket_color_by_course: !!activeSettings.ticket_color_by_course,
      });
      if (ticketColorDiagnosticRef.current.get(ticketKey) !== diagnosticSignature) {
        logClientActivity("kds.ticket_color_evaluated", {
          ticket_instance_id: ticketKey,
          order_id: order?.order_id ?? null,
          diagnostic: colorDiagnostic,
          settings: {
            coursing: !!activeSettings.coursing,
            ticket_color_by_course: !!activeSettings.ticket_color_by_course,
            ticket_color_yellow_after: activeSettings.ticket_color_yellow_after ?? null,
            ticket_color_red_after: activeSettings.ticket_color_red_after ?? null,
            course_color_yellow_after: activeSettings.course_color_yellow_after ?? null,
            course_color_red_after: activeSettings.course_color_red_after ?? null,
          },
        });
        ticketColorDiagnosticRef.current.set(ticketKey, diagnosticSignature);
      }

      if (!shouldPlayTransitions) continue;

      const prevColorState = ticketColorStateRef.current.get(ticketKey);
      if (!prevColorState || prevColorState === nextColorState) continue;

      if (nextColorState === "red" && prevColorState !== "red") {
        playEventSound("ticketRed");
      } else if (nextColorState === "yellow" && prevColorState === "green") {
        playEventSound("ticketYellow");
      }
    }

    ticketColorStateRef.current = nextStates;
    ticketColorDiagnosticRef.current = new Map(
      [...ticketColorDiagnosticRef.current].filter(([ticketKey]) => nextStates.has(ticketKey))
    );
  }, [playEventSound]);

  useEffect(() => {
    ticketColorStateRef.current = new Map();
    ticketColorDiagnosticRef.current = new Map();
    ticketColorSoundInitializedRef.current = false;
  }, [stationId]);

  useEffect(() => {
    if (!stationId) return;

    syncTicketColorStates(ticketColorSoundInitializedRef.current);
    ticketColorSoundInitializedRef.current = true;
  }, [orders, settings, stationId, syncTicketColorStates]);

  useEffect(() => {
    if (!stationId) return;

    const intervalId = window.setInterval(() => {
      if (!ticketColorSoundInitializedRef.current) return;
      syncTicketColorStates(true);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [stationId, syncTicketColorStates]);

  const selectedPrinterMacs = useMemo(() => {
    const selectedIds = Array.isArray(settings?.selected_printer_ids)
      ? settings.selected_printer_ids
      : [];
    const selectedIdSet = new Set(
      selectedIds
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0)
    );

    if (!selectedIdSet.size) return [];

    return availablePrinters
      .filter((printer) => selectedIdSet.has(Number(printer?.id)))
      .map((printer) => String(printer?.macAddress || "").trim())
      .filter(Boolean);
  }, [availablePrinters, settings?.selected_printer_ids]);

  const prevTicketIdsRef = useRef(null);
  useEffect(() => {
    const currentIds = orders.map(o => o.ticket_instance_id);
    const prevIds = prevTicketIdsRef.current;
    if (prevIds == null) {
      prevTicketIdsRef.current = currentIds;
      return;
    }
    const newIds = currentIds.filter(id => !prevIds.includes(id));
    if (newIds.length > 0) {
      newIds.forEach(id => playNewTicketSoundOnce({
        ticket_instance_id: id
      }));
    }
    prevTicketIdsRef.current = currentIds;
  }, [orders, stationId]);
  const prevVoidedItemStateRef = useRef(null);
  useEffect(() => {
    const nextVoidedMap = new Map();
    for (const order of orders || []) {
      const ticketInstanceId = order?.ticket_instance_id ?? null;
      const orderId = order?.order_id ?? null;
      for (const item of order?.items || []) {
        const itemId = item?.ID ?? null;
        if (!itemId) continue;
        const stateKey = normalizeVoidedItemSoundKey({
          ticket_instance_id: ticketInstanceId,
          order_id: orderId,
          item_id: itemId
        });
        if (!stateKey) continue;
        const isVoided = item?.isVoided === true || Number(item?.TransType) === 2 || String(item?.status || "").toLowerCase() === "voided";
        nextVoidedMap.set(stateKey, {
          isVoided,
          payload: {
            ticket_instance_id: ticketInstanceId,
            order_id: orderId,
            item_id: itemId
          }
        });
      }
    }
    const previousVoidedMap = prevVoidedItemStateRef.current;
    if (previousVoidedMap == null) {
      prevVoidedItemStateRef.current = nextVoidedMap;
      return;
    }
    const newlyVoided = [];
    for (const [stateKey, nextEntry] of nextVoidedMap.entries()) {
      const previousEntry = previousVoidedMap.get(stateKey);
      if (nextEntry?.isVoided && !previousEntry?.isVoided) {
        newlyVoided.push(nextEntry.payload);
      }
    }
    if (newlyVoided.length > 0) {
      newlyVoided.forEach(payload => playVoidedItemSoundOnce(payload));
    }
    prevVoidedItemStateRef.current = nextVoidedMap;
  }, [orders, stationId]);
  useEffect(() => {
    const handleNewOrderSound = (payload = {}) => {
      const targetStationId = Number(payload?.station_id);
      const soundDecision = classifyIncomingTicketSound(payload);
      if (Number.isFinite(targetStationId) && Number(targetStationId) !== Number(stationId)) {
        return;
      }
      if (soundDecision.soundKey === "newTicket") {
        playNewTicketSoundOnce(payload);
        return;
      }
      if (soundDecision.soundKey === "addonReceived") {
        playEventSound("addonReceived");
        return;
      }
    };
    socket.on("new-order", handleNewOrderSound);
    return () => {
      socket.off("new-order", handleNewOrderSound);
    };
  }, [classifyIncomingTicketSound, stationId]);
  useEffect(() => {
    if (!soundSettingsLoaded) return;
    if (!audioUnlockedRef.current) return;
    const pendingEvent = pendingSoundEventRef.current;
    if (!pendingEvent) return;
    pendingSoundEventRef.current = null;
    window.setTimeout(() => {
      playEventSound(pendingEvent, {
        fromUnlock: true
      });
    }, 0);
  }, [soundSettingsLoaded, soundSettings, soundOptions]);

  const footerPageIndicatorText = isOnFreeformPage ? "Freeform" : isOnLineItemPage ? "Line Items" : pages.length > 1 ? `Page ${currentPage + 1} of ${pages.length}` : null;

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (
        isOnFreeformPage || isOnLineItemPage ||
        isDragging ||
        isPagePickerOpen ||
        Date.now() < suppressSwipeUntilRef.current
      ) return;
      touchStartX.current = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      if (
        isOnFreeformPage || isOnLineItemPage ||
        isDragging ||
        isPagePickerOpen ||
        Date.now() < suppressSwipeUntilRef.current
      ) return;
      touchEndX.current = e.changedTouches[0].screenX;

      const deltaX = touchEndX.current - touchStartX.current;
      if (deltaX > 50 && activePageIndex > 0) goToDisplayPage(activePageIndex - 1, { sync: true });
      else if (deltaX < -50 && activePageIndex < totalPages - 1)
        goToDisplayPage(activePageIndex + 1, { sync: true });
    };

    const main = document.getElementById("main-content");
    main?.addEventListener("touchstart", handleTouchStart, { passive: true });
    main?.addEventListener("touchend", handleTouchEnd);

    return () => {
      main?.removeEventListener("touchstart", handleTouchStart);
      main?.removeEventListener("touchend", handleTouchEnd);
    };
  }, [activePageIndex, goToDisplayPage, totalPages, isDragging, isOnFreeformPage, isPagePickerOpen]);

  const removeTicketFromUI = (ticket_instance_id) => {
    setOrders((prev) => prev.filter((o) => o.ticket_instance_id !== ticket_instance_id));

    setPages((prev) => {
      const prefix = `${ticket_instance_id}::`;
      const next = prev.map((pg) => pg.filter((k) => !k.startsWith(prefix)));
      const nonEmpty = next.length ? next : [[]];

      if (currentPage >= nonEmpty.length) {
        setCurrentPage(Math.max(0, nonEmpty.length - 1));
      }

      return nonEmpty;
    });
    setFreeformLayouts((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${ticket_instance_id}::`)) {
          delete next[key];
        }
      });
      return next;
    });
    deleteFreeformLayouts({ ticketInstanceId: ticket_instance_id });
  };

  const bumpItemApi = async (ticket_instance_id, itemId, operation_id = null) => {
    const res = await apiFetch(`/orders/${ticket_instance_id}/bump`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ item_id: itemId, sender_station_id: stationId, sender_screen_id: screenMountId, sender_role: role, operation_id }),
    });
    if (!res.ok) throw new Error(`Failed to bump item (${res.status})`);
  };

  const bumpTicketApi = async (ticket_instance_id, operation_id = null) => {
    const res = await apiFetch(`/orders/${ticket_instance_id}/bump`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sender_station_id: stationId, sender_screen_id: screenMountId, sender_role: role, operation_id }),
    });
    if (!res.ok) throw new Error(`Failed to bump ticket (${res.status})`);
  };

  const unbumpItemApi = async (ticket_instance_id, itemId) => {
    const res = await apiFetch(`/orders/${ticket_instance_id}/unbump`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        item_id: itemId,
        sender_station_id: stationId,
        sender_role: role,
        sender_screen_id: screenMountId,
      }),
    });
    if (!res.ok) {
      throw new Error(await res.text());
    }
  };

  const sendPrintJob = async (lines, printerMacs = selectedPrinterMacs) => {
    if (!Array.isArray(printerMacs) || !printerMacs.length) {
      throw new Error("No printers selected for this screen");
    }

    const jobs = printerMacs.map(async (printerMac) => {
      const res = await fetch(PRINT_SERVER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          printerMac,
          lines,
          cut: true,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || `Failed to send print job to ${printerMac}`);
      }

      return res.json().catch(() => null);
    });

    return Promise.all(jobs);
  };

  const sendPrintJobSafely = async (lines, contextLabel) => {
    try {
      await sendPrintJob(lines);
    } catch (err) {
      console.error(`[print-error] ${contextLabel}`, err);
    }
  };

  const buildPrintCourse = (order, courseIndex, settingsOverride = {}) => {
    const filteredItems = (order?.items || []).filter((item) => {
      if (!item) return false;
      if (Number(item.course) !== Number(courseIndex)) return false;
      if (item.isMessage || item.isFireItem || item.isCourseSeparator) return false;
      if (item.isParked || String(item.status || "").toLowerCase() === "parked") return false;
      return true;
    });

    return buildPrintTicket(
      {
        ...order,
        items: filteredItems,
      },
      {
        ...settingsOverride,
        coursing: false,
      }
    );
  };

  const isDisplayBumpableItem = (item = {}) => {
    const status = String(item?.status || "").toLowerCase();
    return !(
      item?.isCourseSeparator ||
      item?.isMessage ||
      item?.isFireItem ||
      item?.isVoided ||
      Number(item?.TransType) === 2 ||
      status === "voided"
    );
  };

  const recomputeCourseDisplayBoxes = (order) => {
    const currentCourseBoxes =
      order?.course_bump_display_boxes && typeof order.course_bump_display_boxes === "object"
        ? order.course_bump_display_boxes
        : {};
    const items = Array.isArray(order?.items) ? order.items : [];
    const nextCourseBoxes = {};

    for (const [courseKey, boxes] of Object.entries(currentCourseBoxes)) {
      const courseNum = Number(courseKey);
      const courseItems = items.filter(
        (item) => Number(item?.course) === courseNum && !item?.isCourseSeparator && !item?.isMessage
      );

      nextCourseBoxes[courseKey] = (Array.isArray(boxes) ? boxes : []).map((box) => {
        const stationIdNum = Number(box?.station_id);
        const stationItems = courseItems.filter((item) => {
          const itemBoxes = Array.isArray(item?.item_bump_display_boxes)
            ? item.item_bump_display_boxes
            : [];
          return itemBoxes.some(
            (itemBox) => Number(itemBox?.station_id) === stationIdNum && itemBox?.received
          );
        });

        const bumpableStationItems = stationItems.filter(isDisplayBumpableItem);
        const bumped =
          stationItems.length > 0 &&
          bumpableStationItems.length > 0 &&
          bumpableStationItems.every((item) =>
            (item.item_bump_display_boxes || []).some(
              (itemBox) =>
                Number(itemBox?.station_id) === stationIdNum &&
                itemBox?.received &&
                itemBox?.bumped
            )
          );

        return {
          ...box,
          received: stationItems.length > 0,
          bumped,
        };
      });
    }

    return nextCourseBoxes;
  };

  const patchLocalDisplayBoxState = (order, itemIds, bumped) => {
    if (!order) return order;
    const targetIds = new Set((Array.isArray(itemIds) ? itemIds : [itemIds]).map((id) => String(id)));
    if (!targetIds.size) return order;

    let changed = false;
    const nextItems = (order.items || []).map((item) => {
      if (!targetIds.has(String(item?.ID))) return item;
      const boxes = Array.isArray(item?.item_bump_display_boxes)
        ? item.item_bump_display_boxes
        : [];
      if (!boxes.length) return item;

      const nextBoxes = boxes.map((box) => {
        if (Number(box?.station_id) !== Number(stationId)) return box;
        changed = true;
        return { ...box, bumped: !!bumped, received: true };
      });

      return { ...item, item_bump_display_boxes: nextBoxes };
    });

    if (!changed) return order;
    const nextOrder = { ...order, items: nextItems };
    return {
      ...nextOrder,
      course_bump_display_boxes: recomputeCourseDisplayBoxes(nextOrder),
    };
  };

  const bumpAllRemainingItemsViaItemEndpoint = async (order) => {
    const ticketId = order.ticket_instance_id;

    const ticketItems = orders.filter(o => o.ticket_instance_id === ticketId).flatMap(o => o.items || []);
    const uniqueItems = [...new Map((ticketItems.length ? ticketItems : order.items || []).map(item => [item.ID, item])).values()];
    const notBumped = uniqueItems.filter(
      (i) => !i.isCourseSeparator && !i.isMessage && !i.isBumped
    );

    if (notBumped.length > 0) {
      const nowIso = new Date().toISOString();
      setOrders((prev) =>
        prev.map((o) =>
          o.ticket_instance_id !== ticketId
            ? o
            : patchLocalDisplayBoxState({
                ...o,
                items: (o.items || []).map((it) =>
                  notBumped.some((x) => x.ID === it.ID)
                    ? { ...it, isBumped: true, status: "bumped", bumpedAt: nowIso }
                    : it
                ),
              }, notBumped.map((x) => x.ID), true)
        )
      );
    }

    for (const item of notBumped) {
      await bumpItemApi(ticketId, item.ID);
      emitBumpItem(ticketId, item, true);
    }

    await bumpTicketApi(ticketId);
  };

  const handleBumpTicket = async (order) => {
    try {
      const shouldPrintTicketOnBump =
        settings?.enable_printing_from_ods && settings?.print_ticket_on_bump;
      const ticketPrintLines = shouldPrintTicketOnBump
        ? buildPrintTicket(order, {
            ...settings,
            hide_bumped_on_print: false,
          })
        : null;

      await bumpAllRemainingItemsViaItemEndpoint(order);

      if (ticketPrintLines) {
        await sendPrintJobSafely(ticketPrintLines, "ticket-on-bump");
      }

      playEventSound("ticketBumped");
      emitBumpTicket(order.ticket_instance_id);
      emitBumpAllCoursesForTicket(order.ticket_instance_id);

      removeTicketFromUI(order.ticket_instance_id);
    } catch (err) {
      console.error("Failed to bump ticket:", err);
    }
  };

  const handleBumpAllTickets = async () => {
    try {
      for (const order of orders) {
        await handleBumpTicket(order);
      }
    } catch (err) {
      console.error("Failed to bump all tickets:", err);
    }
  };

  const isCourseRealItemForAutoPark = (item = {}) => {
    const status = String(item?.status || "").toLowerCase();
    return !(
      !item ||
      item.isCourseSeparator ||
      item.isMessage ||
      item.isFireItem ||
      item.isVoided ||
      item.isParked ||
      Number(item.TransType) === 2 ||
      status === "voided" ||
      status === "parked"
    );
  };

  const wouldItemBumpCompleteCourse = (order, targetItem) => {
    if (!settings?.coursing) return false;
    if (!order || !targetItem) return false;
    const targetCourse = Number(targetItem.course ?? 0);
    const courseItems = (order.items || []).filter(
      (item) =>
        Number(item?.course ?? 0) === targetCourse &&
        isCourseRealItemForAutoPark(item)
    );

    if (!courseItems.length) return false;
    return courseItems.every((item) => {
      if (String(item.ID) === String(targetItem.ID)) return true;
      return item.isBumped || String(item.status || "").toLowerCase() === "bumped";
    });
  };

  const isVisibleAddonForCookTiming = (item = {}) => {
    const isAddon =
      item?.isAddon === true ||
      item?.is_addon === true ||
      item?.is_addon === 1 ||
      item?.is_addon === "1" ||
      item?.isaddon === true ||
      item?.isaddon === 1 ||
      item?.isaddon === "1";
    return isAddon && item?.suppressAddonDisplay !== true;
  };

  const getCookSecondsForTiming = (item = {}) => {
    const cookSeconds = Number(item?.cookTimeSeconds);
    if (Number.isFinite(cookSeconds) && cookSeconds > 0) return Math.round(cookSeconds);
    const minutes = Number(item?.preparationTime ?? item?.PreparationTime ?? 0);
    return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes * 60) : null;
  };

  const isCookTimingCandidate = (item = {}) => {
    const status = String(item?.status || "").toLowerCase();
    if (!item || item.isCourseSeparator || item.isMessage || item.isFireItem) return false;
    if (item.isBumped || item.isParked || item.isVoided || Number(item.TransType) === 2) return false;
    if (status === "bumped" || status === "parked" || status === "voided") return false;
    if (item.prepReadyAt || item.cookTimingStartedManually) return false;
    if (isVisibleAddonForCookTiming(item)) return false;
    return getCookSecondsForTiming(item) != null;
  };

  const getItemPrepSignedSeconds = (item = {}, orderItems = []) => {
    if (!isCookTimingCandidate(item)) return 0;
    const itemCookSeconds = getCookSecondsForTiming(item);
    if (itemCookSeconds == null) return 0;
    const course = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
    const maxCookSeconds = (orderItems || []).reduce((max, candidate) => {
      if (Number(candidate?.course ?? 0) !== course || !isCookTimingCandidate(candidate)) return max;
      const cookSeconds = getCookSecondsForTiming(candidate);
      return cookSeconds == null ? max : Math.max(max, cookSeconds);
    }, 0);
    const prepSeconds = Math.max(0, maxCookSeconds - itemCookSeconds);
    if (!prepSeconds) return 0;
    const createdAt = new Date(item?.createdAt);
    if (Number.isNaN(createdAt.getTime())) return 0;
    const endAt = item?.isParked && item?.parkedAt ? new Date(item.parkedAt) : new Date();
    const elapsedSeconds = Math.max(
      0,
      Math.floor((endAt.getTime() - createdAt.getTime() - (Number(item?.totalPausedSeconds) || 0) * 1000) / 1000)
    );
    return elapsedSeconds - prepSeconds;
  };

  const isDarkenedPrepItem = (item = {}, orderItems = []) => {
    if (settings?.item_prep_display_mode !== "darkened") return false;
    if (!item || item.isCourseSeparator || item.isMessage || item.isFireItem) return false;
    if (item.isBumped || item.isVoided || Number(item.TransType) === 2) return false;
    const status = String(item.status || "").toLowerCase();
    if (status === "voided" || status === "bumped" || status === "parked") return false;
    return getItemPrepSignedSeconds(item, orderItems) < 0;
  };

  const markPrepItemReadyNow = (ticket_instance_id, item_id) => {
    const nowIso = new Date().toISOString();
    setOrders((prev) =>
      prev.map((order) =>
        order.ticket_instance_id !== ticket_instance_id
          ? order
          : {
              ...order,
              items: (order.items || []).map((item) =>
                String(item.ID) !== String(item_id)
                  ? item
                  : {
                      ...item,
                      createdAt: nowIso,
                      preparationTime: 0,
                      PreparationTime: 0,
                      cookStartDelaySeconds: 0,
                      cookTimingStartedManually: true,
                      prepReadyAt: nowIso,
                    }
              ),
            }
      )
    );
  };

  const hasLaterLiveCourse = (order, course) => {
    const targetCourse = Number(course ?? 0);
    if (!Number.isFinite(targetCourse)) return false;
    return (order?.items || []).some((item) => {
      if (!isCourseRealItemForAutoPark(item)) return false;
      if (item.isBumped || String(item.status || "").toLowerCase() === "bumped") return false;
      const itemCourse = Number(item?.course ?? 0);
      return Number.isFinite(itemCourse) && itemCourse > targetCourse;
    });
  };

  const autoParkTicketAfterCourseBump = (order, course, source) => {
    if (settings?.park_ticket_on_course_bump !== true) return false;
    if (settings?.coursing !== true) return false;
    if (!order?.ticket_instance_id || order?.isParked) return false;
    if (!hasLaterLiveCourse(order, course)) {
      console.log("[KDS auto-park] Skipping park after last course bump", {
        ticket_instance_id: order.ticket_instance_id,
        order_id: order.order_id,
        course,
        source,
      });
      return false;
    }

    const ticketId = String(order.ticket_instance_id);
    if (autoCourseParkTicketIdsRef.current.has(ticketId)) return true;

    autoCourseParkTicketIdsRef.current.add(ticketId);
    window.setTimeout(() => {
      autoCourseParkTicketIdsRef.current.delete(ticketId);
    }, 1500);

    console.log("[KDS auto-park] Parking ticket after course bump", {
      ticket_instance_id: order.ticket_instance_id,
      order_id: order.order_id,
      course,
      source,
    });

    parkTicket(order.ticket_instance_id, { unparkTime: null });
    return true;
  };

  const handleBumpItem = async (ticket_instance_id, item_id) => {
    const order = orders.find((o) => o.ticket_instance_id === ticket_instance_id);
    const current = order?.items.find((i) => i.ID === item_id);
    if (!current) return;

    if (current.isCourseSeparator || current.isMessage) return;

    const isVoided =
      current.isVoided ||
      Number(current.TransType) === 2 ||
      String(current.status || "").toLowerCase() === "voided";

    if (isVoided) return;
    if (current.isBumped) return;

    const shouldAutoParkAfterItemBump = wouldItemBumpCompleteCourse(order, current);

    setOrders((prev) =>
      prev.map((o) =>
        o.ticket_instance_id !== ticket_instance_id
          ? o
          : patchLocalDisplayBoxState({
              ...o,
              items: (o.items || []).map((i) =>
                i.ID !== item_id
                  ? i
                  : { ...i, isBumped: true, status: "bumped", bumpedAt: new Date().toISOString() }
              ),
            }, item_id, true)
      )
    );

    try {
      await bumpItemApi(ticket_instance_id, item_id);
      emitBumpItem(ticket_instance_id, current, true);
      if (shouldAutoParkAfterItemBump) {
        autoParkTicketAfterCourseBump(order, current.course, "item-bump");
      }
    } catch (err) {
      console.error("Failed to bump item:", err);

      setOrders((prev) =>
        prev.map((o) =>
          o.ticket_instance_id !== ticket_instance_id
            ? o
            : patchLocalDisplayBoxState({
                ...o,
                items: (o.items || []).map((i) =>
                  i.ID !== item_id ? i : { ...i, isBumped: false, bumpedAt: null }
                ),
              }, item_id, false)
        )
      );
    }
  };

  const handleToggleItemByDoubleTap = async (ticket_instance_id, item_id) => {
    const order = orders.find((o) => o.ticket_instance_id === ticket_instance_id);
    const current = order?.items.find((i) => i.ID === item_id);
    if (!current) return;

    if (current.isCourseSeparator || current.isMessage) return;

    const isVoided =
      current.isVoided ||
      Number(current.TransType) === 2 ||
      String(current.status || "").toLowerCase() === "voided";

    if (isVoided) return;

    if (!current.isBumped && isDarkenedPrepItem(current, order?.items || [])) {
      markPrepItemReadyNow(ticket_instance_id, item_id);
      return;
    }

    if (!current.isBumped) {
      return handleBumpItem(ticket_instance_id, item_id);
    }

    setOrders((prev) =>
      prev.map((o) =>
        o.ticket_instance_id !== ticket_instance_id
          ? o
          : patchLocalDisplayBoxState({
              ...o,
              items: (o.items || []).map((i) =>
                i.ID !== item_id
                  ? i
                  : { ...i, isBumped: false, status: "pending", bumpedAt: null }
              ),
            }, item_id, false)
      )
    );

    try {
      await unbumpItemApi(ticket_instance_id, item_id);
    } catch (err) {
      console.error("Failed to unbump item:", err);

      setOrders((prev) =>
        prev.map((o) =>
          o.ticket_instance_id !== ticket_instance_id
            ? o
            : patchLocalDisplayBoxState({
                ...o,
                items: (o.items || []).map((i) =>
                  i.ID !== item_id ? i : { ...i, isBumped: true, status: "bumped" }
                ),
              }, item_id, true)
        )
      );
    }
  };

  const isBumpBarActive =
    settings?.enable_bump_bar === true &&
    !isOnFreeformPage &&
    !isOnLineItemPage &&
    !showSettings &&
    !isDragging &&
    !isPagePickerOpen &&
    !isExposeModalOpen &&
    !isSortModalOpen &&
    !isLineItemSortModalOpen &&
    !isParkOptionsModalOpen &&
    !isUnparkModalOpen &&
    !peekedOrder &&
    !isPeekMode &&
    !isFireCourseMode &&
    !isFireTicketMode &&
    !isFireItemMode &&
    !isPrintCourseMode &&
    !isPrintTicketMode &&
    !isParkTicketMode &&
    !isPinTicketMode;

  const bumpSelectedTicketFromBumpBar = useCallback(async () => {
    const selectedOrder = getSelectedBumpBarOrder();
    if (!selectedOrder) return;

    const currentIndex = selectableBumpBarPageCards.findIndex(
      (card) => card.key === bumpBarSelectedTicketKey
    );
    const nextSelectableCard =
      currentIndex >= 0 && selectableBumpBarPageCards.length > 1
        ? selectableBumpBarPageCards[
            (currentIndex + 1) % selectableBumpBarPageCards.length
          ]
        : null;

    await handleBumpTicket(selectedOrder);

    setBumpBarSelectionMode("ticket");
    setBumpBarSelectedTicketKey(nextSelectableCard?.key || null);
    setBumpBarSelectedItemId(null);
    setBumpBarSelectedPageControl(null);
  }, [
    bumpBarSelectedTicketKey,
    getSelectedBumpBarOrder,
    handleBumpTicket,
    selectableBumpBarPageCards,
  ]);

  const handleBumpBarRedraw = useCallback(async () => {
    if (bumpBarSelectionMode === "ticket" && activateBumpBarSelectedPageControl()) {
      return;
    }

    if (bumpBarSelectionMode !== "ticket") return;
    const selectedOrder = getSelectedBumpBarOrder();
    if (!selectedOrder) return;

    const selectableItems = getBumpBarSelectableItems(selectedOrder);
    if (!selectableItems.length) {
      await bumpSelectedTicketFromBumpBar();
      return;
    }

    setBumpBarSelectionMode("item");
    setBumpBarSelectedItemId(selectableItems[0].ID);
  }, [
    activateBumpBarSelectedPageControl,
    bumpBarSelectionMode,
    bumpSelectedTicketFromBumpBar,
    getBumpBarSelectableItems,
    getSelectedBumpBarOrder,
  ]);

  const handleBumpBarBump = useCallback(async () => {
    if (bumpBarSelectionMode === "item") {
      const selectedOrder = getSelectedBumpBarOrder();
      if (!selectedOrder || bumpBarSelectedItemId == null) return;
      await handleToggleItemByDoubleTap(
        selectedOrder.ticket_instance_id,
        bumpBarSelectedItemId
      );
      return;
    }

    await bumpSelectedTicketFromBumpBar();
  }, [
    bumpBarSelectedItemId,
    bumpBarSelectionMode,
    bumpSelectedTicketFromBumpBar,
    getSelectedBumpBarOrder,
    handleToggleItemByDoubleTap,
  ]);

  const handleBumpBarRecall = useCallback(async () => {
    if (bumpBarSelectionMode === "item") {
      const selectedOrder = getSelectedBumpBarOrder();
      const selectedItem = (selectedOrder?.items || []).find(
        (item) => String(item?.ID) === String(bumpBarSelectedItemId)
      );
      if (selectedItem?.isBumped) {
        await handleToggleItemByDoubleTap(
          selectedOrder.ticket_instance_id,
          bumpBarSelectedItemId
        );
        return;
      }
    }

    setBumpBarUnbumpMenuSignal((value) => value + 1);
  }, [
    bumpBarSelectedItemId,
    bumpBarSelectionMode,
    getSelectedBumpBarOrder,
    handleToggleItemByDoubleTap,
  ]);

  useEffect(() => {
    if (settings?.enable_bump_bar !== true || isOnFreeformPage || isOnLineItemPage) {
      clearBumpBarSelection();
      return;
    }

    if (bumpBarSelectedPageControl) {
      const isInvalidPageControl =
        (bumpBarSelectedPageControl === "previous" && activePageIndex <= 0) ||
        (bumpBarSelectedPageControl === "next" && activePageIndex >= totalPages - 1);
      if (isInvalidPageControl) clearBumpBarSelection();
      return;
    }

    if (!bumpBarSelectedTicketKey) return;
    const card = pageCards.find((candidate) => candidate.key === bumpBarSelectedTicketKey);
    if (!card || !isBumpBarSelectableTicket(card.order)) {
      clearBumpBarSelection();
      return;
    }

    if (bumpBarSelectionMode !== "item") return;

    const selectableItems = getBumpBarSelectableItems(card.order);
    if (!selectableItems.length) {
      setBumpBarSelectionMode("ticket");
      setBumpBarSelectedItemId(null);
      setBumpBarSelectedPageControl(null);
      return;
    }

    if (
      bumpBarSelectedItemId == null ||
      !selectableItems.some((item) => String(item.ID) === String(bumpBarSelectedItemId))
    ) {
      setBumpBarSelectedItemId(selectableItems[0].ID);
    }
  }, [
    bumpBarSelectedItemId,
    bumpBarSelectedPageControl,
    bumpBarSelectedTicketKey,
    bumpBarSelectionMode,
    activePageIndex,
    clearBumpBarSelection,
    getBumpBarSelectableItems,
    isOnFreeformPage,
    isOnLineItemPage,
    pageCards,
    settings?.enable_bump_bar,
    totalPages,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isBumpBarActive || isBumpBarIgnoredTarget(event.target)) return;

      const key = event.key;
      const code = event.code;

      if (key === "ArrowLeft") {
        event.preventDefault();
        if (!event.repeat && bumpBarSelectionMode === "item") {
          setBumpBarSelectionMode("ticket");
          setBumpBarSelectedItemId(null);
        }
        return;
      }

      if (key === "ArrowRight") {
        event.preventDefault();
        if (bumpBarSelectionMode === "item") moveBumpBarItemSelection(-1);
        else moveBumpBarTicketSelection(-1);
        return;
      }

      if (key === " ") {
        event.preventDefault();
        if (bumpBarSelectionMode === "item") moveBumpBarItemSelection(1);
        else moveBumpBarTicketSelection(1);
        return;
      }

      if (key === "ArrowDown") {
        event.preventDefault();
        if (bumpBarSelectionMode === "item") return;
        if (!event.repeat) setBumpBarSummarySignal((value) => value + 1);
        return;
      }

      if (key === "Control") {
        event.preventDefault();
        if (!event.repeat) handleBumpBarBump();
        return;
      }

      if (code === "NumpadSubtract" || key === "-") {
        event.preventDefault();
        if (bumpBarSelectionMode === "item") return;
        if (!event.repeat) handleBumpBarRecall();
        return;
      }

      if (/^[0-9]$/.test(key)) {
        event.preventDefault();
        const slotIndex = Number(key);
        if (bumpBarSelectionMode === "item") {
          selectBumpBarItemBySlot(slotIndex);
        } else if (slotIndex <= 5) {
          selectBumpBarTicketBySlot(slotIndex);
        }
        return;
      }

      if (key === "Enter") {
        event.preventDefault();
        if (!event.repeat) handleBumpBarRedraw();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    bumpBarSelectionMode,
    handleBumpBarBump,
    handleBumpBarRecall,
    handleBumpBarRedraw,
    isBumpBarActive,
    moveBumpBarItemSelection,
    moveBumpBarTicketSelection,
    selectBumpBarItemBySlot,
    selectBumpBarTicketBySlot,
  ]);

  const handleBumpCourseFromCard = async (
    ticket_instance_id,
    courseNumber,
    courseItems = []
  ) => {
    const bumpable = courseItems.filter(
      (i) => !i.isCourseSeparator && !i.isMessage && !i.isBumped
    );
    if (bumpable.length === 0) return;
    const operation_id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    logClientActivity("kds.bump_course_requested", {
      operation_id, station_id: stationId, ticket_instance_id, course: courseNumber,
      item_ids: bumpable.map(item => item.ID),
    });

    const selectedOrder = orders.find((o) => o.ticket_instance_id === ticket_instance_id);
    const shouldPrintCourseOnBump =
      settings?.enable_printing_from_ods && settings?.print_course_on_bump;
    const coursePrintLines =
      shouldPrintCourseOnBump && selectedOrder
        ? buildPrintCourse(selectedOrder, courseNumber, {
            ...settings,
            hide_bumped_on_print: false,
          })
        : null;

    try {
      const nowIso = new Date().toISOString();
      setOrders((prev) =>
        prev.map((o) =>
          o.ticket_instance_id !== ticket_instance_id
            ? o
            : patchLocalDisplayBoxState({
                ...o,
                items: (o.items || []).map((it) =>
                  bumpable.some((x) => x.ID === it.ID)
                    ? { ...it, isBumped: true, status: "bumped", bumpedAt: nowIso }
                    : it
                ),
              }, bumpable.map((x) => x.ID), true)
        )
      );

      for (const item of bumpable) {
        await bumpItemApi(ticket_instance_id, item.ID, operation_id);
        emitBumpItem(ticket_instance_id, item, true, { operation_id });
      }

      if (coursePrintLines) {
        await sendPrintJobSafely(coursePrintLines, "course-on-bump");
      }

      emitBumpCourse(ticket_instance_id, Number(courseNumber), { operation_id });
      logClientActivity("kds.bump_course_locally_applied", {
        operation_id, station_id: stationId, ticket_instance_id, course: courseNumber,
        item_ids: bumpable.map(item => item.ID),
      });
      playEventSound("courseBumped");
      if (selectedOrder) {
        autoParkTicketAfterCourseBump(selectedOrder, courseNumber, "course-bump");
      }
    } catch (err) {
      console.error("Failed to bump course:", err);
    }
  };

  const handleCourseComplete = async (ticket_instance_id) => {
    try {
      const order = orders.find((o) => o.ticket_instance_id === ticket_instance_id);
      if (settings?.park_ticket_on_course_bump === true && order) {
        const didAutoPark = autoParkTicketAfterCourseBump(
          order,
          order.items?.find((item) => isCourseRealItemForAutoPark(item) && !item.isBumped)?.course ?? null,
          "last-course-complete"
        );
        if (didAutoPark) return;
      }

      const shouldPrintTicketOnBump =
        settings?.enable_printing_from_ods && settings?.print_ticket_on_bump;
      const ticketPrintLines =
        shouldPrintTicketOnBump && order
          ? buildPrintTicket(order, {
              ...settings,
              hide_bumped_on_print: false,
            })
          : null;

      if (order) await bumpAllRemainingItemsViaItemEndpoint(order);
      else await bumpTicketApi(ticket_instance_id);

      if (ticketPrintLines) {
        await sendPrintJobSafely(ticketPrintLines, "ticket-on-last-course-bump");
      }

      playEventSound("ticketBumped");
      emitBumpTicket(ticket_instance_id);
      removeTicketFromUI(ticket_instance_id);
    } catch (err) {
      console.error("Failed to finalize ticket on last course:", err);
    }
  };

  const handleSelectFireCourse = ({ ticketId, orderId, course }) => {
    if (!isFireCourseMode) return;
    fireCourse(course, { ticket_instance_id: ticketId, order_id: orderId });
    playEventSound("courseFired");
    setIsPeekMode(false);
    setIsFireCourseMode(false);
  };

  const handleSelectFireItem = ({ ticketId, orderId, itemId }) => {
    if (!isFireItemMode) return;
    fireItem(itemId, { ticket_instance_id: ticketId, order_id: orderId });
    playEventSound("courseFired");
    setIsPeekMode(false);
    setIsFireItemMode(false);
  };

  const handleFireTicketSelect = (ticket) => {
    if (!isFireTicketMode) return;

    setIsPeekMode(false);
    setIsFireTicketMode(false);
    setPeekModeIntent(null);

    if (settings.coursing) {
      const courses = new Set(
        ticket.items
          .filter((i) => i.course != null && !i.isFired && !i.isBumped)
          .map((i) => Number(i.course))
          .filter(Number.isFinite)
      );

      [...courses]
        .sort((a, b) => a - b)
        .forEach((course) => {
          fireCourse(course, {
            ticket_instance_id: ticket.ticket_instance_id,
            order_id: ticket.order_id,
          });
        });

      emitFireTicket({
        ticket_instance_id: ticket.ticket_instance_id,
        order_id: ticket.order_id,
      });
    } else {
      FireAllItems({
        ticket_instance_id: ticket.ticket_instance_id,
        order_id: ticket.order_id,
      });
    }

    playEventSound("courseFired");
  };

  const handleSelectPrintCourse = async ({ ticketId, orderId, course }) => {
    if (!isPrintCourseMode) return null;

    const payload = {
      ticket_instance_id: ticketId,
      order_id: orderId ?? null,
      course,
    };

    console.log("[print-select] course", payload);

    try {
      const selectedOrder = orders.find(
        (o) => o.ticket_instance_id === ticketId
      );

      if (!selectedOrder) {
        console.error("[print-error] course order not found");
        return null;
      }

      const lines = buildPrintCourse(selectedOrder, course, settings);

      console.log("[print-lines-course]", lines);

      await sendPrintJob(lines);

      console.log("[print-success] course printed");
    } catch (err) {
      console.error("[print-error] course", err);
    } finally {
      setIsPeekMode(false);
      setIsPrintCourseMode(false);
    }

    return payload;
  };

  const handlePrintTicketSelect = async (ticket) => {
    if (!isPrintTicketMode) return null;

    const payload = {
      ticket_instance_id: ticket.ticket_instance_id,
      order_id: ticket.order_id ?? null,
      ticket,
    };

    console.log("[print-select] ticket", payload);

    try {
      const lines = buildPrintTicket(ticket, settings);

      console.log("[print-lines-ticket]", lines);

      await sendPrintJob(lines);

      console.log("[print-success] ticket printed");
    } catch (err) {
      console.error("[print-error] ticket", err);
    } finally {
      setIsPeekMode(false);
      setIsPrintTicketMode(false);
      setPeekModeIntent(null);
    }

    return payload;
  };

  const handleEnterParkingMode = () => {
    setIsPeekMode(true);
    setIsParkTicketMode(true);
    setPeekModeIntent("park");
  };

  const resetParkOptions = () => {
    setParkMode(PARK_MODE_MANUAL);
    setSpecificUnparkTime("");
    setDurationHours("0");
    setDurationMinutes("15");
    setParkSelectionTicket(null);
    setIsParkOptionsModalOpen(false);
  };

  const setDurationFromTotalMinutes = (totalMinutes) => {
    const bounded = Math.max(0, Math.min(23 * 60 + 59, totalMinutes));
    setDurationHours(String(Math.floor(bounded / 60)));
    setDurationMinutes(String(bounded % 60));
  };

  const adjustDurationByMinutes = (deltaMinutes) => {
    setParkMode(PARK_MODE_DURATION);
    const currentTotal =
      (Math.max(0, Number(durationHours) || 0) * 60) +
      Math.max(0, Number(durationMinutes) || 0);
    setDurationFromTotalMinutes(currentTotal + deltaMinutes);
  };

  const applyDurationPreset = (minutes) => {
    setParkMode(PARK_MODE_DURATION);
    setDurationFromTotalMinutes(minutes);
  };

  const applySpecificTimePreset = (minutesFromNow) => {
    setParkMode(PARK_MODE_SPECIFIC);
    setSpecificUnparkTime(
      formatTimeInputInZone(new Date(Date.now() + minutesFromNow * 60 * 1000), stationTimezone)
    );
  };

  const handleSelectParkTicket = async (ticket) => {
    if (!isParkTicketMode) return;

    setIsPeekMode(false);
    setIsParkTicketMode(false);
    setPeekModeIntent(null);
    setParkSelectionTicket(ticket);
    setIsParkOptionsModalOpen(true);
  };

  const handleLineItemRowSelect = (row) => {
    const ticket = row?.order;
    const item = row?.item;
    if (!ticket || !item) return;

    if (peekModeIntent === "view") {
      setPeekedOrder(ticket);
      setIsPeekMode(false);
      return;
    }
    if (peekModeIntent === "pin" && isPinTicketMode) {
      pinLineItemTicketToNormalPages(keyForOrder(ticket));
      return;
    }
    if (isFireTicketMode) {
      handleFireTicketSelect(ticket);
      return;
    }
    if (isPrintTicketMode) {
      handlePrintTicketSelect(ticket);
      return;
    }
    if (isParkTicketMode) {
      handleSelectParkTicket(ticket);
      return;
    }
    if (isFireCourseMode) {
      handleSelectFireCourse({
        ticketId: ticket.ticket_instance_id,
        orderId: ticket.order_id,
        course: Number(item.course),
      });
      return;
    }
    if (isPrintCourseMode) {
      handleSelectPrintCourse({
        ticketId: ticket.ticket_instance_id,
        orderId: ticket.order_id,
        course: Number(item.course),
      });
      return;
    }
    if (isFireItemMode) {
      handleSelectFireItem({
        ticketId: ticket.ticket_instance_id,
        orderId: ticket.order_id,
        itemId: item.ID,
      });
    }
  };

  const handleConfirmParkTicket = async () => {
    if (!parkSelectionTicket?.ticket_instance_id) return;

    const scheduledUnparkIso = buildScheduledUnparkIso({
      parkMode,
      stationTimezone,
      specificTime: specificUnparkTime,
      durationHours,
      durationMinutes,
    });

    if (parkMode !== PARK_MODE_MANUAL && !scheduledUnparkIso) return;

    parkTicket(parkSelectionTicket.ticket_instance_id, {
      unparkTime: scheduledUnparkIso,
    });
    resetParkOptions();
  };

  const handleSelectUnparkTicket = async (ticket) => {
    unparkTicket(ticket.ticket_instance_id);
    setIsUnparkModalOpen(false);
    setSelectedParkedTicketId(null);
  };

  const handleOpenUnparkModal = () => {
    setIsPeekMode(false);
    setIsParkTicketMode(false);
    setPeekModeIntent(null);
    setSelectedParkedTicketId(parkedOrdersForMenu[0]?.ticket_instance_id ?? null);
    setIsUnparkModalOpen(true);
  };

  const displayName =
    stationName ||
    (role === "expo"
      ? "Expo"
      : role
        ? role.charAt(0).toUpperCase() + role.slice(1)
        : `Station ${stationId}`);

  const scheduledUnparkPreview = buildScheduledUnparkIso({
    parkMode,
    stationTimezone,
    specificTime: specificUnparkTime,
    durationHours,
    durationMinutes,
  });

  const parkedOrdersForMenu = useMemo(() => {
    const toTime = (value) => {
      const ms = Date.parse(value || "");
      return Number.isFinite(ms) ? ms : 0;
    };

    return [...orders]
      .filter((o) => o.isParked)
      .sort((a, b) => {
        const parkDiff = toTime(b.parkedAt ?? b.parked_at) - toTime(a.parkedAt ?? a.parked_at);
        if (parkDiff !== 0) return parkDiff;
        return String(b.ticket_instance_id || "").localeCompare(String(a.ticket_instance_id || ""));
      });
  }, [orders]);

  const selectedParkedOrder =
    parkedOrdersForMenu.find((o) => o.ticket_instance_id === selectedParkedTicketId) ||
    parkedOrdersForMenu[0] ||
    null;

  const scheduledUnparkLabel =
    parkMode === PARK_MODE_MANUAL
      ? "Manual unpark"
      : scheduledUnparkPreview
        ? formatScheduledPreview(scheduledUnparkPreview, stationTimezone)
        : parkMode === PARK_MODE_SPECIFIC
          ? "Select a local time"
          : "Enter a duration";
  const isBumpBarItemSelectMode =
    settings?.enable_bump_bar === true &&
    !isOnFreeformPage &&
    !isOnLineItemPage &&
    bumpBarSelectionMode === "item";

  return (
    <div className="relative flex h-screen flex-col overflow-hidden font-sans bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100">
      {!isAudioUnlocked && (
        <div className="absolute inset-x-0 top-20 z-40 flex justify-center px-4 pointer-events-none" role="status">
          <div className="pointer-events-auto rounded-2xl border border-amber-300 bg-amber-100/95 px-5 py-3 text-center text-sm font-semibold text-amber-950 shadow-lg dark:border-amber-200 dark:bg-amber-300/95 dark:text-slate-950">
            Tap anywhere to enable sound alerts
          </div>
        </div>
      )}
      <TopBar
        stationId={stationId}
        displayName={displayName}
        onToggleDarkMode={toggleDarkMode}
        isDark={isDark}
        onOpenSettings={() => setShowSettings(true)}
        onOpenTutorial={onOpenTutorial}
        onBumpAll={handleBumpAllTickets}
        onPinClick={() => {
          if (isPinTicketMode) {
            setIsPeekMode(false);
            setIsPinTicketMode(false);
            setPeekModeIntent(null);
            return;
          }
          setIsPeekMode(true);
          setIsFireTicketMode(false);
          setIsFireCourseMode(false);
          setIsFireItemMode(false);
          setIsPrintTicketMode(false);
          setIsPrintCourseMode(false);
          setIsParkTicketMode(false);
          setIsPinTicketMode(true);
          setPeekModeIntent("pin");
        }}
        onExposeClick={() => {
          setSelectedExposeTicketKey((current) => current || freeformCardsForMenu[0]?.key || null);
          setIsExposeModalOpen(true);
        }}
        onSortClick={() => {
          if (isOnLineItemPage) {
            setIsLineItemSortModalOpen(true);
            return;
          }
          setIsSortModalOpen(true);
        }}
        showExposeButton={
          isOnFreeformPage &&
          !isPeekMode &&
          !isFireCourseMode &&
          !isFireItemMode &&
          !isPrintCourseMode
        }
        showSortButton={
          (isOnFreeformPage || isOnLineItemPage) &&
          !isPeekMode &&
          !isFireCourseMode &&
          !isFireItemMode &&
          !isPrintCourseMode
        }
        showPinButton={
          (isOnFreeformPage || isOnLineItemPage) &&
          (!isPeekMode || isPinTicketMode) &&
          !isFireCourseMode &&
          !isFireItemMode &&
          !isPrintCourseMode
        }
        isPinActive={isPinTicketMode}
        showAxisLockButtons={
          isOnFreeformPage &&
          !isPeekMode &&
          !isFireCourseMode &&
          !isFireItemMode &&
          !isPrintCourseMode
        }
        onToggleLockXAxis={() => setIsFreeformLockXAxis((current) => !current)}
        isLockXAxisActive={isFreeformLockXAxis}
        onToggleLockYAxis={() => setIsFreeformLockYAxis((current) => !current)}
        isLockYAxisActive={isFreeformLockYAxis}
      />

      {totalPages > 1 && (
        <>
          {activePageIndex > 0 && (
            <button
              style={{ zIndex: 30 }}
                className={`absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/10 dark:bg-gray-800/20 text-white dark:text-gray-100 border border-white/20 dark:border-white/15 shadow-md rounded-full w-12 h-12 flex items-center justify-center z-30 transition-colors active:bg-white/80 hover:bg-white/80 dark:hover:bg-gray-700/85 dark:active:bg-gray-700/85 ${
                  settings?.enable_bump_bar === true && bumpBarSelectedPageControl === "previous"
                    ? "ring-4 ring-yellow-400 shadow-yellow-300/40"
                    : ""
                }`}
                onClick={() => goToDisplayPage(activePageIndex - 1, { sync: true })}
                aria-label="Previous Page"
              >
                <ChevronLeft size={26} strokeWidth={2.5} />
              </button>
            )}
            {activePageIndex < totalPages - 1 && (
            <button
              style={{ zIndex: 30 }}
                className={`absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/10 dark:bg-gray-800/20 text-white dark:text-gray-100 border border-white/20 dark:border-white/15 shadow-md rounded-full w-12 h-12 flex items-center justify-center z-30 transition-colors active:bg-white/80 hover:bg-white/80 dark:hover:bg-gray-700/85 dark:active:bg-gray-700/85 ${
                  settings?.enable_bump_bar === true && bumpBarSelectedPageControl === "next"
                    ? "ring-4 ring-yellow-400 shadow-yellow-300/40"
                    : ""
                }`}
                onClick={() => goToDisplayPage(activePageIndex + 1, { sync: true })}
                aria-label="Next Page"
              >
                <ChevronRight size={26} strokeWidth={2.5} />
              </button>
          )}
        </>
      )}

      {peekedOrder && peekModeIntent === "view" && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70"
          onClick={() => {
            setPeekedOrder(null);
            setPeekModeIntent(null);
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 p-4 rounded shadow max-w-xl w-full max-h-[90vh] overflow-y-auto relative z-[60] flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <TicketCard
              order={peekedOrder}
              orderIndex={0}
              totalCards={1}
              isPeekMode={isPeekMode}
              settings={{
                ...settings,
                hide_bumped_courses: false,
                hide_course: false,
                item_prep_display_mode: "normal",
              }}
              onItemTap={() => {}}
              onItemDoubleTap={() => {}}
              onBump={() => {}}
              onBumpCourse={(ticketId, courseNumber, courseItems) =>
                handleBumpCourseFromCard(ticketId, courseNumber, courseItems)
              }
              onCourseComplete={handleCourseComplete}
              isPrepScreen={false}
              isFireCourseMode={isFireCourseMode}
              onSelectFireCourse={handleSelectFireCourse}
              setIsFireCourseMode={setIsFireCourseMode}
              isFireItemMode={isFireItemMode}
              onSelectFireItem={handleSelectFireItem}
            />
          </div>
        </div>
      )}

      {isPeekMode && (
        <div className="fixed inset-0 z-40 bg-gray-400/50 dark:bg-gray-800/30 backdrop-blur-sm pointer-events-auto" />
      )}

      {isPeekMode && (
        <div className="fixed bottom-[5.5rem] left-1/2 transform -translate-x-1/2 z-[200] flex items-center gap-6">
          <button
            onClick={() => {
              setIsPeekMode(false);
              setIsFireTicketMode(false);
              setIsFireCourseMode(false);
              setIsFireItemMode(false);
              setIsPrintTicketMode(false);
              setIsPrintCourseMode(false);
              setIsParkTicketMode(false);
              setIsPinTicketMode(false);
              setPeekModeIntent(null);
            }}
            className="px-4 py-2 rounded shadow border-2 border-yellow-400 bg-yellow-300 text-black shadow-yellow-300/30 font-semibold uppercase hover:bg-yellow-400"
          >
            Cancel
          </button>
          {isParkTicketMode && (
            <button
              onClick={handleOpenUnparkModal}
              className="px-5 py-2 rounded shadow border-2 border-cyan-500 bg-cyan-500 text-white dark:bg-cyan-400 dark:text-slate-950 dark:border-cyan-300 shadow-cyan-500/30 font-semibold uppercase hover:bg-cyan-600 dark:hover:bg-cyan-300"
            >
              Unpark Ticket
            </button>
          )}
        </div>
      )}

      {(isFireCourseMode || isFireItemMode || isPrintCourseMode || isBumpBarItemSelectMode) && (
        <div className="fixed inset-0 z-40 bg-gray-400/50 dark:bg-gray-800/30 backdrop-blur-sm pointer-events-auto" />
      )}

      {(isFireCourseMode || isFireItemMode || isPrintCourseMode) && (
        <button
          onClick={() => {
            setIsFireCourseMode(false);
            setIsFireItemMode(false);
            setIsPrintCourseMode(false);
          }}
          className="fixed bottom-[5.5rem] left-1/2 transform -translate-x-1/2 z-[200] px-4 py-2 rounded shadow border-2 border-yellow-400 bg-yellow-300 text-black shadow-yellow-300/30 font-semibold uppercase hover:bg-yellow-400"
        >
          {isPrintCourseMode ? "Cancel Print Mode" : "Cancel Fire Mode"}
        </button>
      )}

        <main
          id="main-content"
          className={`scrollbar-hide min-h-0 flex-1 ${isOnLineItemPage ? "overflow-hidden p-0" : "overflow-y-auto px-4 py-2 pb-4"} ${
            isOnFreeformPage ? "bg-red-100 dark:bg-red-950/30" : "bg-gray-100 dark:bg-gray-900"
            }`}
      >
        {isOnFreeformPage ? (
          <FreeformBoard
            stationId={stationId}
            cards={freeformCards}
            layouts={freeformLayouts}
            sortRequest={freeformSortRequest}
            onBringToFront={bringFreeformToFront}
            onPersistLayout={persistFreeformLayout}
            onDragStateChange={setIsDragging}
            onItemTap={(ticketInstanceId, itemId) => handleBumpItem(ticketInstanceId, itemId)}
            onItemDoubleTap={(ticketInstanceId, itemId) =>
              handleToggleItemByDoubleTap(ticketInstanceId, itemId)
            }
            onPrepItemReady={markPrepItemReadyNow}
            onBump={handleBumpTicket}
            settings={settings}
            isPeekMode={isPeekMode}
            isFireCourseMode={isFireCourseMode}
            onSelectFireCourse={handleSelectFireCourse}
            setIsFireCourseMode={setIsFireCourseMode}
            isFireItemMode={isFireItemMode}
            onSelectFireItem={handleSelectFireItem}
            isPrintCourseMode={isPrintCourseMode}
            onSelectPrintCourse={handleSelectPrintCourse}
            isPrintTicketMode={isPrintTicketMode}
            setPeekedOrder={setPeekedOrder}
            setPeekModeIntent={setPeekModeIntent}
            onBumpCourse={(ticketId, courseNumber, courseItems) =>
              handleBumpCourseFromCard(ticketId, courseNumber, courseItems)
            }
            onCourseComplete={handleCourseComplete}
            onPeekSelect={(selectedOrder) => {
              if (peekModeIntent === "view") {
                setPeekedOrder(selectedOrder);
                setIsPeekMode(false);
              } else if (peekModeIntent === "fire" && isFireTicketMode) {
                handleFireTicketSelect(selectedOrder);
                setPeekModeIntent(null);
              } else if (peekModeIntent === "print" && isPrintTicketMode) {
                handlePrintTicketSelect(selectedOrder);
              } else if (peekModeIntent === "park" && isParkTicketMode) {
                handleSelectParkTicket(selectedOrder);
                setPeekModeIntent(null);
              } else if (peekModeIntent === "pin" && isPinTicketMode) {
                pinTicketToNormalPages(keyForOrder(selectedOrder));
                setIsPeekMode(false);
                setIsPinTicketMode(false);
                setPeekModeIntent(null);
              }
            }}
            isLockXAxisActive={isFreeformLockXAxis}
            isLockYAxisActive={isFreeformLockYAxis}
          />
        ) : isOnLineItemPage ? (
          <LineItemBoard
            orders={Array.from(lineItemKeySet).map((key) => orderByKey[key]).filter(Boolean)}
            settings={settings}
            onBumpItem={handleBumpItem}
            onPrepItemReady={markPrepItemReadyNow}
            rowOrder={lineItemRowOrder}
            onRowOrderChange={persistLineItemRowOrder}
            isActionMode={isPeekMode || isFireCourseMode || isFireItemMode || isPrintCourseMode || isFireTicketMode || isPrintTicketMode || isParkTicketMode || isPinTicketMode}
            onSelectRow={handleLineItemRowSelect}
          />
        ) : (
        <DndTicketBoard
          cards={pageCards}
          onDragStateChange={(dragging) => {
            suppressSwipeUntilRef.current = Date.now() + 500;
            setIsDragging(dragging);
          }}
          onReorder={handleBoardReorder}
          onOpenPagePicker={openPagePickerForKey}
          boardClassName={`relative pointer-events-auto flex flex-nowrap items-start justify-start gap-1 ${
            isPeekMode || isFireCourseMode || isFireItemMode || isPrintCourseMode || isBumpBarItemSelectMode ? "z-50" : "z-20"
          }`}
          boardStyle={{
            width: `calc(100% - ${rightScrollGutterPx}px)`,
            "--kds-board-column-width": `calc((100% - ${(ticketWidthSlotCount - 1) * ticketGapPx}px) / ${ticketWidthSlotCount})`,
            "--kds-ticket-max-width": `calc((100% - ${(ticketWidthSlotCount - 1) * ticketGapPx}px) / ${ticketWidthSlotCount})`,
          }}
          renderCard={({ order, orderIndex }, dragState = {}) => {
            const ticketKey = `${order.ticket_instance_id}::${order.card ?? 0}`;
            const isBumpBarSelectedTicket =
              settings?.enable_bump_bar === true &&
              bumpBarSelectedTicketKey === ticketKey;
            const shouldDimForBumpBarItemMode =
              isBumpBarItemSelectMode && !isBumpBarSelectedTicket;

            return (
            <TicketCard
              key={ticketKey}
              order={order}
              orderIndex={orderIndex}
              totalPages={1}
              disableBottomCutoffFade={!!(dragState?.isDragging || dragState?.isOverlay)}
              rootClassName={`kds-dnd-item ${
                isBumpBarItemSelectMode && isBumpBarSelectedTicket
                  ? "relative z-50"
                  : shouldDimForBumpBarItemMode
                    ? "blur-[2px] opacity-45 scale-[0.98]"
                    : ""
              }`}
              dragHandleMode="header"
              dragHandleClassName="drag-handle"
              dragHandleProps={dragState?.dragHandleProps || {}}
              onItemTap={(ticketInstanceId, itemId) =>
                handleBumpItem(ticketInstanceId, itemId)
              }
              onItemDoubleTap={(ticketInstanceId, itemId) =>
                handleToggleItemByDoubleTap(ticketInstanceId, itemId)
              }
              onPrepItemReady={markPrepItemReadyNow}
              onBump={handleBumpTicket}
              settings={settings}
              isPeekMode={isPeekMode}
              isFireCourseMode={isFireCourseMode}
              onSelectFireCourse={handleSelectFireCourse}
              setIsFireCourseMode={setIsFireCourseMode}
              isFireItemMode={isFireItemMode}
              onSelectFireItem={handleSelectFireItem}
              isPrintCourseMode={isPrintCourseMode}
              onSelectPrintCourse={handleSelectPrintCourse}
              isPrintTicketMode={isPrintTicketMode}
              isBumpBarTicketSelected={
                settings?.enable_bump_bar === true &&
                bumpBarSelectionMode === "ticket" &&
                bumpBarSelectedTicketKey === ticketKey
              }
              isBumpBarItemMode={
                settings?.enable_bump_bar === true &&
                bumpBarSelectionMode === "item" &&
                bumpBarSelectedTicketKey === ticketKey
              }
              bumpBarSelectedItemId={bumpBarSelectedItemId}
              setPeekedOrder={setPeekedOrder}
              setPeekModeIntent={setPeekModeIntent}
              onBumpCourse={(ticketId, courseNumber, courseItems) =>
                handleBumpCourseFromCard(ticketId, courseNumber, courseItems)
              }
              onCourseComplete={handleCourseComplete}
              onDragHandleTouchStart={() => suppressSwipeForDragHandle()}
              onPeekSelect={(selectedOrder) => {
                if (peekModeIntent === "view") {
                  setPeekedOrder(selectedOrder);
                  setIsPeekMode(false);
                } else if (peekModeIntent === "fire" && isFireTicketMode) {
                  handleFireTicketSelect(selectedOrder);
                  setPeekModeIntent(null);
                } else if (peekModeIntent === "print" && isPrintTicketMode) {
                  handlePrintTicketSelect(selectedOrder);
                } else if (peekModeIntent === "park" && isParkTicketMode) {
                  handleSelectParkTicket(selectedOrder);
                  setPeekModeIntent(null);
                } else if (peekModeIntent === "pin" && isPinTicketMode) {
                  pinTicketToNormalPages(keyForOrder(selectedOrder));
                  setIsPeekMode(false);
                  setIsPinTicketMode(false);
                  setPeekModeIntent(null);
                }
              }}
            />
            );
          }}
        />
        )}

        {isPagePickerOpen && (
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60"
            onClick={() => {
              setIsPagePickerOpen(false);
              setPendingDropKey(null);
            }}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl max-w-2xl w-[90%]"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                Send ticket to page…
              </h3>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {freeformEnabled && (
                  <button
                    onClick={() => handlePickPage("freeform")}
                    className="rounded-xl border px-4 py-6 text-center font-semibold shadow hover:shadow-md focus:outline-none focus:ring-2 bg-red-100 border-red-300 text-red-900 dark:bg-red-950/40 dark:border-red-800 dark:text-red-100"
                  >
                    <div className="text-2xl">F</div>
                    <div className="text-xs opacity-70">Freeform</div>
                  </button>
                )}
                {lineItemBoardEnabled && (
                  <button
                    onClick={() => handlePickPage("line_items")}
                    className="rounded-xl border px-4 py-6 text-center font-semibold shadow hover:shadow-md focus:outline-none focus:ring-2 bg-blue-100 border-blue-300 text-blue-900 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-100"
                  >
                    <div className="text-2xl">L</div>
                    <div className="text-xs opacity-70">Line Items</div>
                  </button>
                )}
                {pages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePickPage(idx)}
                    className={`rounded-xl border px-4 py-6 text-center font-semibold shadow hover:shadow-md focus:outline-none focus:ring-2 ${
                      idx === currentPage
                        ? "bg-yellow-200 border-yellow-400 text-gray-900"
                        : "bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                    }`}
                  >
                    <div className="text-2xl">{idx + 1}</div>
                    <div className="text-xs opacity-70">Normal Page</div>
                  </button>
                ))}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsPagePickerOpen(false);
                    setPendingDropKey(null);
                  }}
                  className="px-4 py-2 rounded-md border bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {isExposeModalOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60"
          onClick={() => {
            setIsExposeModalOpen(false);
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-xl w-[95vw] max-w-7xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold mb-3 text-gray-900 dark:text-gray-100">
              Expose Ticket
            </h2>

            {freeformCardsForMenu.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                No tickets on the freeform board.
              </div>
            ) : (
              <div className="flex h-[72vh] gap-4">
                <div className="w-[320px] shrink-0 border-r border-gray-200 dark:border-gray-700 pr-3 overflow-y-auto">
                  <div className="space-y-2">
                    {freeformCardsForMenu.map((ticket) => {
                      const isSelected = selectedExposeTicketKey === ticket.key;
                      return (
                        <button
                          key={ticket.key}
                          type="button"
                          onClick={() => setSelectedExposeTicketKey(ticket.key)}
                          onDoubleClick={async () => {
                            await exposeFreeformTicket(ticket.key);
                            setSelectedExposeTicketKey(ticket.key);
                            setIsExposeModalOpen(false);
                          }}
                          className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                            isSelected
                              ? "border-emerald-500 bg-emerald-50 dark:border-emerald-400 dark:bg-emerald-500/10"
                              : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60"
                          }`}
                        >
                          <div className="text-sm font-semibold">
                            #{ticket.order.order_id} - {ticket.order.table_name || "No table"}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {ticket.order.server_name || "No server"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="min-w-0 flex-1 overflow-y-auto">
                  {selectedExposeOrder ? (
                    <div className="flex flex-col items-center">
                      <div className="mb-3 flex w-full max-w-xl items-center justify-between gap-3">
                        <div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Selected ticket
                          </div>
                          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            #{selectedExposeOrder.order_id} - {selectedExposeOrder.table_name || "No table"}
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (!selectedExposeTicketKey) return;
                            await exposeFreeformTicket(selectedExposeTicketKey);
                            setIsExposeModalOpen(false);
                          }}
                          className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 active:scale-[0.98] touch-manipulation"
                        >
                          Expose
                        </button>
                      </div>

                      <div className="w-full flex justify-center">
                        <div className="max-w-xl">
                          <TicketCard
                            order={selectedExposeOrder}
                            orderIndex={0}
                            totalCards={1}
                            isPeekMode
                            settings={settings}
                            onItemTap={() => {}}
                            onItemDoubleTap={() => {}}
                            onBump={() => {}}
                            onBumpCourse={() => {}}
                            onCourseComplete={() => {}}
                            isPrepScreen={false}
                            isFireCourseMode={false}
                            onSelectFireCourse={() => {}}
                            setIsFireCourseMode={() => {}}
                            isFireItemMode={false}
                            onSelectFireItem={() => {}}
                            isPrintCourseMode={false}
                            onSelectPrintCourse={() => {}}
                            isFireTicketMode={false}
                            isPrintTicketMode={false}
                            setPeekedOrder={() => {}}
                            setPeekModeIntent={() => {}}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            <div className="mt-3 text-right">
              <button
                onClick={() => setIsExposeModalOpen(false)}
                className="px-3 py-1 text-sm rounded bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isLineItemSortModalOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60"
          onClick={() => setIsLineItemSortModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-xl w-[92vw] max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Sort Line Items
            </h2>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              Choose a one-time row sort. You can still drag items up or down afterward.
            </p>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {LINE_ITEM_SORT_OPTIONS.map((option) => (
                <button
                  key={option.mode}
                  onClick={() => applyLineItemSort(option.mode)}
                  className="min-h-[78px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm font-semibold leading-snug text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                >
                  {option.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsLineItemSortModalOpen(false)}
              className="mt-4 w-full h-14 text-lg font-semibold rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 active:scale-[0.98] touch-manipulation"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {isSortModalOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60"
          onClick={() => {
            setIsSortModalOpen(false);
            setPendingSortMode(null);
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-xl w-[92vw] max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Sort Freeform Tickets
            </h2>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              Choose a one-time layout preset. You can still move tickets freely afterward.
            </p>

            {!pendingSortMode ? (
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-3">
                  <div className="px-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Column
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPendingSortMode("column-by-oldest")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Age: Oldest
                    </button>
                    <button
                      onClick={() => setPendingSortMode("column-by-newest")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Age: Newest
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPendingSortMode("column-by-most-items")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Items: Most
                    </button>
                    <button
                      onClick={() => setPendingSortMode("column-by-least-items")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Items: Least
                    </button>
                  </div>
                  <button
                    onClick={() => setPendingSortMode("course-columns")}
                    className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                  >
                    Current Course
                  </button>
                  <button
                    onClick={() => setPendingSortMode("column-fired-first")}
                    className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                  >
                    Fired First
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="px-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Rows
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPendingSortMode("rows-by-oldest")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Age: Oldest
                    </button>
                    <button
                      onClick={() => setPendingSortMode("rows-by-newest")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Age: Newest
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPendingSortMode("rows-by-most-items")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Items: Most
                    </button>
                    <button
                      onClick={() => setPendingSortMode("rows-by-least-items")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Items: Least
                    </button>
                  </div>
                  <button
                    onClick={() => setPendingSortMode("rows-by-current-course")}
                    className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                  >
                    Current Course
                  </button>
                  <button
                    onClick={() => setPendingSortMode("rows-fired-first")}
                    className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                  >
                    Fired First
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="px-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Misc
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPendingSortMode("stack-by-oldest")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Diag Age: Oldest
                    </button>
                    <button
                      onClick={() => setPendingSortMode("stack-by-newest")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Diag Age: Newest
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPendingSortMode("stack-by-most-items")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Diag Items: Most
                    </button>
                    <button
                      onClick={() => setPendingSortMode("stack-by-least-items")}
                      className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                    >
                      Diag Items: Least
                    </button>
                  </div>
                  <button
                    onClick={() => setPendingSortMode("compact-pack")}
                    className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                  >
                    Compact Pack
                  </button>
                  <button
                    onClick={() => setPendingSortMode("dense-grid")}
                    className="w-full min-h-[76px] rounded-xl bg-slate-700 px-4 py-4 text-left text-sm leading-snug font-semibold text-white hover:bg-slate-800 active:scale-[0.98] touch-manipulation"
                  >
                    Dense Grid
                  </button>
                </div>

                <button
                  onClick={() => {
                    setIsSortModalOpen(false);
                    setPendingSortMode(null);
                  }}
                  className="md:col-span-3 w-full h-14 text-lg font-semibold rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 active:scale-[0.98] touch-manipulation"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-1 gap-3">
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
                  Are you sure you want to run the `{getSortModeLabel(pendingSortMode)}` layout?
                </div>
                <button
                  onClick={() => {
                    setIsFreeformLockXAxis(false);
                    setIsFreeformLockYAxis(false);
                    setFreeformSortRequest({ id: Date.now(), mode: pendingSortMode });
                    setIsSortModalOpen(false);
                    setPendingSortMode(null);
                  }}
                  className="w-full h-14 text-lg font-semibold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98] touch-manipulation"
                >
                  Yes, Sort Tickets
                </button>
                <button
                  onClick={() => setPendingSortMode(null)}
                  className="w-full h-14 text-lg font-semibold rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 active:scale-[0.98] touch-manipulation"
                >
                  Back
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="relative z-30 shrink-0">
        <FooterBar
          role={role}
          isDark={isDark}
          screenMountId={screenMountId}
          lastOrderId={
            orders.length > 0
              ? (orders[orders.length - 1].order_id ?? orders[orders.length - 1].ID ?? null)
              : null
          }
          refreshOrders={setOrders}
          orders={orders}
          stationId={stationId}
          currentPage={activePageIndex}
          totalPages={totalPages}
          pageIndicatorText={footerPageIndicatorText}
          isLineItemMode={isOnLineItemPage}
          onFireTicket={() => {
            setIsPeekMode(true);
            setIsFireTicketMode(true);
            setIsFireCourseMode(false);
            setIsFireItemMode(false);
            setIsPrintTicketMode(false);
            setIsPrintCourseMode(false);
            setIsParkTicketMode(false);
            setIsPinTicketMode(false);
            setPeekModeIntent("fire");
          }}
          onFireCourse={() => {
            setIsPeekMode(false);
            setIsFireTicketMode(false);
            setIsFireCourseMode(true);
            setIsFireItemMode(false);
            setIsPrintTicketMode(false);
            setIsPrintCourseMode(false);
            setIsParkTicketMode(false);
            setIsPinTicketMode(false);
            setPeekModeIntent(null);
          }}
          onFireItem={() => {
            setIsPeekMode(false);
            setIsFireTicketMode(false);
            setIsFireCourseMode(false);
            setIsFireItemMode(true);
            setIsPrintTicketMode(false);
            setIsPrintCourseMode(false);
            setIsParkTicketMode(false);
            setIsPinTicketMode(false);
            setPeekModeIntent(null);
          }}
          onPrintTicket={() => {
            setIsPeekMode(true);
            setIsFireTicketMode(false);
            setIsFireCourseMode(false);
            setIsFireItemMode(false);
            setIsPrintTicketMode(true);
            setIsPrintCourseMode(false);
            setIsParkTicketMode(false);
            setIsPinTicketMode(false);
            setPeekModeIntent("print");
          }}
          onPrintCourse={() => {
            setIsPeekMode(false);
            setIsFireTicketMode(false);
            setIsFireCourseMode(false);
            setIsFireItemMode(false);
            setIsPrintTicketMode(false);
            setIsPrintCourseMode(true);
            setIsParkTicketMode(false);
            setIsPinTicketMode(false);
            setPeekModeIntent(null);
          }}
          onViewFullTicket={() => {
            setIsPeekMode(true);
            setIsPinTicketMode(false);
            setPeekModeIntent("view");
          }}
          onPinClick={() => {
            setIsPeekMode(true);
            setIsFireTicketMode(false);
            setIsFireCourseMode(false);
            setIsFireItemMode(false);
            setIsPrintTicketMode(false);
            setIsPrintCourseMode(false);
            setIsParkTicketMode(false);
            setIsPinTicketMode(true);
            setPeekModeIntent("pin");
          }}
          onExposeClick={() => {
            setSelectedExposeTicketKey((current) => current || freeformCardsForMenu[0]?.key || null);
            setIsExposeModalOpen(true);
          }}
          onSortClick={() => setIsSortModalOpen(true)}
          showExposeButton={false}
          showSortButton={false}
          showPinButton={false}
          settings={settings}
          summaryToggleSignal={bumpBarSummarySignal}
          unbumpMenuSignal={bumpBarUnbumpMenuSignal}
          onParkClick={handleEnterParkingMode}
          onTicketUnbumped={emitUnbumpTicket}
          refreshFromServer={fetchOrders}
        />
      </div>

      {showSettings && (
        <SettingsModal stationId={stationId} onClose={() => setShowSettings(false)} />
      )}

      {isParkOptionsModalOpen && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60"
          onClick={resetParkOptions}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Park Ticket #{parkSelectionTicket?.order_id}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Choose how this ticket should unpark. Specific times use {stationTimezone}.
            </p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => setParkMode(PARK_MODE_MANUAL)}
                className={`rounded-2xl border px-4 py-4 text-left min-h-[96px] ${
                  parkMode === PARK_MODE_MANUAL
                    ? "border-amber-500 bg-amber-100 text-amber-950 dark:bg-amber-500/20 dark:text-amber-100"
                    : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                }`}
              >
                <div className="text-base font-semibold">Manual</div>
                <div className="mt-1 text-sm opacity-80">Unpark later by hand.</div>
              </button>
              <button
                onClick={() => setParkMode(PARK_MODE_SPECIFIC)}
                className={`rounded-2xl border px-4 py-4 text-left min-h-[96px] ${
                  parkMode === PARK_MODE_SPECIFIC
                    ? "border-amber-500 bg-amber-100 text-amber-950 dark:bg-amber-500/20 dark:text-amber-100"
                    : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                }`}
              >
                <div className="text-base font-semibold">Specific Time</div>
                <div className="mt-1 text-sm opacity-80">Pick a local clock time.</div>
              </button>
              <button
                onClick={() => setParkMode(PARK_MODE_DURATION)}
                className={`rounded-2xl border px-4 py-4 text-left min-h-[96px] ${
                  parkMode === PARK_MODE_DURATION
                    ? "border-amber-500 bg-amber-100 text-amber-950 dark:bg-amber-500/20 dark:text-amber-100"
                    : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                }`}
              >
                <div className="text-base font-semibold">After Duration</div>
                <div className="mt-1 text-sm opacity-80">Set hours and minutes.</div>
              </button>
            </div>

            <div className="mt-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4">
              {parkMode === PARK_MODE_MANUAL && (
                <div className="min-h-[132px] flex flex-col justify-center">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Manual unpark</div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    No timer will be set. The ticket stays parked until someone unparks it.
                  </div>
                </div>
              )}

              {parkMode === PARK_MODE_SPECIFIC && (
                <div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Choose a local unpark time</div>
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Uses {stationTimezone}. If the chosen time already passed today, it rolls to tomorrow.
                  </div>
                  <input
                    type="time"
                    value={specificUnparkTime}
                    onChange={(e) => {
                      setParkMode(PARK_MODE_SPECIFIC);
                      setSpecificUnparkTime(e.target.value);
                    }}
                    className="mt-4 w-full rounded-2xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-5 text-2xl font-semibold text-center"
                  />
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {specificTimePresetMinutes.map((minutes) => (
                      <button
                        key={minutes}
                        onClick={() => applySpecificTimePreset(minutes)}
                        className="rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-3 py-4 text-base font-semibold"
                      >
                        +{minutes}m
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {parkMode === PARK_MODE_DURATION && (
                <div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Set a countdown</div>
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Use presets or adjust the timer in 15-minute steps.
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {durationPresetMinutes.map((minutes) => (
                      <button
                        key={minutes}
                        onClick={() => applyDurationPreset(minutes)}
                        className="rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-3 py-4 text-base font-semibold"
                      >
                        {minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`}
                      </button>
                    ))}
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 p-3">
                      <div className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400">Hours</div>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        inputMode="numeric"
                        value={durationHours}
                        onChange={(e) => {
                          setParkMode(PARK_MODE_DURATION);
                          setDurationHours(e.target.value);
                        }}
                        className="mt-2 w-full bg-transparent text-center text-4xl font-bold outline-none"
                      />
                    </div>
                    <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 p-3">
                      <div className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400">Minutes</div>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        inputMode="numeric"
                        value={durationMinutes}
                        onChange={(e) => {
                          setParkMode(PARK_MODE_DURATION);
                          setDurationMinutes(e.target.value);
                        }}
                        className="mt-2 w-full bg-transparent text-center text-4xl font-bold outline-none"
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-4 gap-3">
                    <button
                      onClick={() => adjustDurationByMinutes(-15)}
                      className="rounded-xl bg-gray-200 dark:bg-gray-700 px-3 py-4 text-base font-semibold"
                    >
                      -15m
                    </button>
                    <button
                      onClick={() => adjustDurationByMinutes(-5)}
                      className="rounded-xl bg-gray-200 dark:bg-gray-700 px-3 py-4 text-base font-semibold"
                    >
                      -5m
                    </button>
                    <button
                      onClick={() => adjustDurationByMinutes(5)}
                      className="rounded-xl bg-amber-100 dark:bg-amber-500/20 px-3 py-4 text-base font-semibold"
                    >
                      +5m
                    </button>
                    <button
                      onClick={() => adjustDurationByMinutes(15)}
                      className="rounded-xl bg-amber-100 dark:bg-amber-500/20 px-3 py-4 text-base font-semibold"
                    >
                      +15m
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 rounded-lg bg-gray-100 dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
              Scheduled unpark: {scheduledUnparkLabel}
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={resetParkOptions}
                className="px-4 py-2 rounded-md border bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmParkTicket}
                disabled={parkMode !== PARK_MODE_MANUAL && !scheduledUnparkPreview}
                className="px-4 py-2 rounded-md bg-amber-500 text-black font-semibold disabled:opacity-50"
              >
                Park Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {isUnparkModalOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60"
          onClick={() => {
            setIsUnparkModalOpen(false);
            setSelectedParkedTicketId(null);
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-xl w-[95vw] max-w-7xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold mb-3 text-gray-900 dark:text-gray-100">
              Unpark Ticket
            </h2>

            {parkedOrdersForMenu.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                No parked tickets.
              </div>
            ) : (
              <div className="flex h-[72vh] gap-4">
                <div className="w-[320px] shrink-0 border-r border-gray-200 dark:border-gray-700 pr-3 overflow-y-auto">
                  <div className="space-y-2">
                    {parkedOrdersForMenu.map((ticket) => {
                      const isSelected =
                        selectedParkedOrder?.ticket_instance_id === ticket.ticket_instance_id;

                      return (
                        <button
                          key={ticket.ticket_instance_id}
                          type="button"
                          onClick={() => setSelectedParkedTicketId(ticket.ticket_instance_id)}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            setSelectedParkedTicketId(ticket.ticket_instance_id);
                          }}
                          className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                            isSelected
                              ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10"
                              : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60"
                          }`}
                        >
                          <div className="text-sm font-semibold">
                            #{ticket.order_id} - {ticket.table_name || "No table"}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {ticket.server_name || "No server"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="min-w-0 flex-1 overflow-y-auto">
                  {selectedParkedOrder ? (
                    <div className="flex flex-col items-center">
                      <div className="mb-3 flex w-full max-w-xl items-center justify-between gap-3">
                        <div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Previewing parked ticket
                          </div>
                          <div className="text-base font-semibold">
                            #{selectedParkedOrder.order_id} - {selectedParkedOrder.table_name || "No table"}
                          </div>
                        </div>
                        <button
                          onClick={() => handleSelectUnparkTicket(selectedParkedOrder)}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            handleSelectUnparkTicket(selectedParkedOrder);
                          }}
                          className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 active:scale-[0.98] touch-manipulation"
                        >
                          Unpark
                        </button>
                      </div>

                      <div className="w-full flex justify-center">
                        <div className="max-w-xl">
                          <TicketCard
                            order={selectedParkedOrder}
                            orderIndex={0}
                            totalCards={1}
                            isPeekMode
                            settings={settings}
                            onItemTap={() => {}}
                            onItemDoubleTap={() => {}}
                            onBump={() => {}}
                            onBumpCourse={() => {}}
                            onCourseComplete={() => {}}
                            isPrepScreen={false}
                            isFireCourseMode={false}
                            onSelectFireCourse={() => {}}
                            setIsFireCourseMode={() => {}}
                            isFireItemMode={false}
                            onSelectFireItem={() => {}}
                            isPrintCourseMode={false}
                            onSelectPrintCourse={() => {}}
                            isFireTicketMode={false}
                            isPrintTicketMode={false}
                            setPeekedOrder={() => {}}
                            setPeekModeIntent={() => {}}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {false ? (
            <ul className="max-h-64 overflow-y-auto space-y-2 text-sm">
              {orders.filter((o) => o.isParked).length === 0 ? (
                <li className="text-gray-500 dark:text-gray-400">No parked tickets.</li>
              ) : (
                orders
                  .filter((o) => o.isParked)
                  .map((o) => (
                    <li
                      key={o.ticket_instance_id}
                      className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-1"
                    >
                      <span>
                        #{o.order_id} — {o.table_name || "No table"}
                      </span>
                      <button
                        onClick={() => handleSelectUnparkTicket(o)}
                        className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Unpark
                      </button>
                    </li>
                  ))
              )}
            </ul>
            ) : null}

            <div className="mt-3 text-right">
              <button
                onClick={() => setIsUnparkModalOpen(false)}
                className="px-3 py-1 text-sm rounded bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
